import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoImage from '../assets/images/logo.png';
import bgImage from '../assets/images/bg.jpg';
import './Recruiter.css';


const VIOLATION_LABELS = {
  tab_change: 'Pindah Tab',
  window_blur: 'Keluar Jendela',
  face_missing: 'Wajah Tidak Terdeteksi',
  face_multiple: 'Banyak Wajah',
  copy_attempt: 'Copy-Paste',
  fullscreen_exit: 'Keluar Fullscreen'
};

const loadImageAsDataUrl = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    } catch (error) {
      reject(error);
    }
  };
  image.onerror = reject;
  image.src = src;
});

let tutorialAssetsPromise = null;

const getTutorialAssets = () => {
  if (!tutorialAssetsPromise) {
    tutorialAssetsPromise = Promise.all([
      loadImageAsDataUrl(logoImage),
      loadImageAsDataUrl(bgImage),
    ]).then(([logoDataUrl, bgDataUrl]) => ({ logoDataUrl, bgDataUrl }));
  }
  return tutorialAssetsPromise;
};

const renderTutorialCoverPage = (doc, assets, config) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const accent = config.accent || [13, 122, 122];
  const title = config.title || 'Panduan';
  const subtitle = config.subtitle || '';
  const bullets = Array.isArray(config.bullets) ? config.bullets.slice(0, 4) : [];

  doc.setFillColor(247, 250, 252);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  doc.setFillColor(accent[0], accent[1], accent[2]);
  doc.rect(0, 0, pageWidth, 48, 'F');

  if (assets?.logoDataUrl) {
    doc.addImage(assets.logoDataUrl, 'PNG', 14, 10, 62, 22);
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(config.kicker || 'Panduan penggunaan dashboard', 14, 38);

  if (assets?.bgDataUrl) {
    doc.addImage(assets.bgDataUrl, 'PNG', pageWidth - 66, 9, 52, 69);
  }

  doc.setFillColor(255, 255, 255);
  doc.roundedRect(14, 58, pageWidth - 28, 54, 4, 4, 'F');
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 20, 76);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  const subtitleLines = doc.splitTextToSize(subtitle, pageWidth - 40);
  doc.text(subtitleLines, 20, 87);

  doc.setFillColor(255, 255, 255);
  doc.roundedRect(14, 122, pageWidth - 28, 82, 4, 4, 'F');
  doc.setFillColor(accent[0], accent[1], accent[2]);
  doc.roundedRect(20, 128, 60, 12, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Yang akan dipelajari', 24, 136);

  doc.setTextColor(31, 41, 55);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  let bulletY = 147;
  bullets.forEach((item) => {
    const lines = doc.splitTextToSize(item, pageWidth - 50);
    doc.text(`• ${lines[0]}`, 20, bulletY);
    if (lines.length > 1) {
      doc.text(lines.slice(1), 26, bulletY + 5);
      bulletY += (lines.length * 5) + 2;
    } else {
      bulletY += 9;
    }
  });

  doc.setTextColor(75, 85, 99);
  doc.setFontSize(9);
  doc.text(config.footer || 'Dokumen ini dibuat langsung dari halaman Recruiter/Admin.', 14, 286);
};

const PDF_MARGIN = 14;
const PDF_CONTENT_BOTTOM = 278;
const PDF_ACCENT = [13, 122, 122];
const PDF_ACCENT_DARK = [10, 92, 92];
const PDF_TEXT = [31, 41, 55];
const PDF_MUTED = [107, 114, 128];
const PDF_BORDER = [226, 232, 240];
const PDF_SURFACE = [247, 250, 252];

const getPdfDecoratedPages = (doc) => {
  if (!doc.__pdfDecoratedPages) {
    doc.__pdfDecoratedPages = new Set();
  }
  return doc.__pdfDecoratedPages;
};

const drawPdfHeader = (doc, { title, subtitle, kicker }) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...PDF_ACCENT);
  doc.rect(0, 0, pageWidth, 30, 'F');
  doc.setFillColor(...PDF_ACCENT_DARK);
  doc.rect(0, 27, pageWidth, 3, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.text(String(kicker || 'Sistem Rekrutmen').toUpperCase(), PDF_MARGIN, 9);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(title, PDF_MARGIN, 19);

  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const lines = doc.splitTextToSize(subtitle, pageWidth - (PDF_MARGIN * 2));
    doc.text(lines[0], PDF_MARGIN, 25.5);
  }

  getPdfDecoratedPages(doc).add(1);
  doc.setTextColor(...PDF_TEXT);
  return 40;
};

const drawPdfContinuationHeader = (doc, title) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...PDF_ACCENT);
  doc.rect(0, 0, pageWidth, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(`${title} - lanjutan`, PDF_MARGIN, 9);
  doc.setTextColor(...PDF_TEXT);
  return 22;
};

const drawPdfFooter = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const totalPages = doc.getNumberOfPages();

  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(...PDF_BORDER);
    doc.setLineWidth(0.3);
    doc.line(PDF_MARGIN, pageHeight - 13, pageWidth - PDF_MARGIN, pageHeight - 13);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...PDF_MUTED);
    doc.text('Dokumen dibuat otomatis oleh sistem rekrutmen', PDF_MARGIN, pageHeight - 8);
    doc.text(`Halaman ${page} dari ${totalPages}`, pageWidth - PDF_MARGIN, pageHeight - 8, { align: 'right' });
  }

  doc.setTextColor(...PDF_TEXT);
};

const drawPdfInfoTable = (doc, startY, rows) => {
  autoTable(doc, {
    startY,
    theme: 'grid',
    margin: { left: PDF_MARGIN, right: PDF_MARGIN, top: 24 },
    styles: {
      font: 'helvetica',
      fontSize: 9.5,
      cellPadding: 3,
      textColor: PDF_TEXT,
      lineColor: PDF_BORDER,
      lineWidth: 0.2,
      valign: 'middle',
    },
    columnStyles: {
      0: { cellWidth: 45, fontStyle: 'bold', fillColor: PDF_SURFACE, textColor: [71, 85, 105] },
    },
    body: rows,
  });

  return doc.lastAutoTable.finalY;
};

const drawPdfSectionTitle = (doc, title, startY) => {
  doc.setFillColor(...PDF_ACCENT);
  doc.roundedRect(PDF_MARGIN, startY, 2.5, 7, 1, 1, 'F');
  doc.setTextColor(...PDF_ACCENT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(title, PDF_MARGIN + 6, startY + 5.3);
  doc.setTextColor(...PDF_TEXT);
  return startY + 12;
};

const drawPdfTable = (doc, { startY, head, body, columnStyles, title, styles }) => {
  autoTable(doc, {
    startY,
    head,
    body,
    theme: 'grid',
    margin: { left: PDF_MARGIN, right: PDF_MARGIN, top: 20, bottom: 18 },
    styles: {
      font: 'helvetica',
      fontSize: 8.5,
      cellPadding: 2.5,
      textColor: PDF_TEXT,
      lineColor: PDF_BORDER,
      lineWidth: 0.2,
      valign: 'top',
      overflow: 'linebreak',
      ...(styles || {}),
    },
    headStyles: {
      fillColor: PDF_ACCENT,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.8,
    },
    alternateRowStyles: { fillColor: [249, 251, 252] },
    columnStyles,
    willDrawPage: () => {
      const decoratedPages = getPdfDecoratedPages(doc);
      const pageNumber = doc.getCurrentPageInfo().pageNumber;
      if (pageNumber > 1 && !decoratedPages.has(pageNumber)) {
        drawPdfContinuationHeader(doc, title);
        decoratedPages.add(pageNumber);
      }
    },
  });

  return doc.lastAutoTable.finalY;
};

const ensurePdfSpace = (doc, currentY, neededHeight, title) => {
  if (currentY + neededHeight <= PDF_CONTENT_BOTTOM) {
    return currentY;
  }

  doc.addPage();
  const decoratedPages = getPdfDecoratedPages(doc);
  const pageNumber = doc.getCurrentPageInfo().pageNumber;
  const nextY = drawPdfContinuationHeader(doc, title);
  decoratedPages.add(pageNumber);
  return nextY;
};

export default function RecruiterDashboard() {
  const { user, logout } = useAuth();
  const userMenuRef = useRef(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('candidates');
  
  // Candidates/Sessions
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  // const [detail, setDetail] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(true);
  // const [detailLoading, setDetailLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [showCandidateDetailModal, setShowCandidateDetailModal] = useState(false);
  const [candidateDetail, setCandidateDetail] = useState(null);
  const [candidateDetailLoading, setCandidateDetailLoading] = useState(false);
  const [showSessionDetailModal, setShowSessionDetailModal] = useState(false);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [sessionDetailLoading, setSessionDetailLoading] = useState(false);
  const [showPsychotestDetailModal, setShowPsychotestDetailModal] = useState(false);
  const [psychotestDetail, setPsychotestDetail] = useState(null);
  const [editingCandidateId, setEditingCandidateId] = useState(null);
  const [showExamQuestionsModal, setShowExamQuestionsModal] = useState(false);
  const [examModalExam, setExamModalExam] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [recruiters, setRecruiters] = useState([]);
  const [positions, setPositions] = useState([]);
  const [editingPositionId, setEditingPositionId] = useState(null);
  const [positionForm, setPositionForm] = useState ({
    position_cd: '',
    position_name: '',
    departement: ''
  });
  const [loadingRecruiters, setLoadingRecruiters] = useState(false);
  const [showRecruiterForm, setShowRecruiterForm] = useState(false);
  const [showPositionForm, setShowPositionForm] = useState (false);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [editingRecruiterId, setEditingRecruiterId] = useState(null);
  const [recruiterForm, setRecruiterForm] = useState({
    name: '',
    email: '',
    password: ''
  });

  const [invitationSchedules, setInvitationSchedules] = useState(['']);
  const [selectedViolations, setSelectedViolations] = useState(new Set());

  const [showProfileSection, setShowProfileSection] = useState(true);
  
  // Exams management
  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);
  const [showExamForm, setShowExamForm] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [examForm, setExamForm] = useState({ 
    title: '', 
    description: '', 
    duration_minutes: 60, 
    max_violations: 1,
    total: 0,
    multiple_choice: 1,
    passing_grade: 60,
    position: 'all'
  });
  const [selectedExams, setSelectedExams] = useState([]);
  
  // Questions management for selected exam
  const [selectedExamForQuestions, setSelectedExamForQuestions] = useState(null);
  const [expandedExams, setExpandedExams] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    option_e: '',
    correct_answer: 'a',
    points: 10,
    photo: null
  });
  const [questionPhotoFile, setQuestionPhotoFile] = useState(null);
  const [questionPhotoPreview, setQuestionPhotoPreview] = useState(null);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [selectAllQuestions, setSelectAllQuestions] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  
  // Pagination
  const [candidatePage, setCandidatePage] = useState(1);
  const [candidatePerPage, setCandidatePerPage] = useState('10');
  const [questionPage, setQuestionPage] = useState(1);
  const [questionPerPage, setQuestionPerPage] = useState('10');
  const [detailExamPage, setDetailExamPage] = useState(1);
  const [detailExamPerPage, setDetailExamPerPage] = useState('10');
  const [showPsychotestGroups, setShowPsychotestGroups] = useState(false);
  
  // Upload instructions modal
  const [showUploadInstructions, setShowUploadInstructions] = useState(false);
  const [uploadInstructionsType, setUploadInstructionsType] = useState('multiple-choice'); 
  
  // Candidate registration
  const [showCandidateForm, setShowCandidateForm] = useState(false);
  const [candidateForm, setCandidateForm] = useState({
    name: '',
    email: '',
    password: '',
    position: 'general'
  });
  const [candidateScheduleModules, setCandidateScheduleModules] = useState([]);
  const [candidateFormExams, setCandidateFormExams] = useState([]);
  const [registering, setRegistering] = useState(false);
  // const [editingCandidateSchedule, setEditingCandidateSchedule] = useState(null);
  const [editingCandidateScheduleModuleIndex, setEditingCandidateScheduleModuleIndex] = useState(null);
  const [editingCandidateScheduleTime, setEditingCandidateScheduleTime] = useState({ starts_at: '', ends_at: '' });
  const [candidateModuleStatuses, setCandidateModuleStatuses] = useState({}); 
  const [candidateExcelFile, setCandidateExcelFile] = useState(null);
  const [uploadingCandidates, setUploadingCandidates] = useState(false);


  // Psychotest management
  const [psychotestQuestions, setPsychotestQuestions] = useState([]);
  const [loadingPsychotest, setLoadingPsychotest] = useState(true);
  const [showPsychotestForm, setShowPsychotestForm] = useState(false);
  const [editingPsychotestQuestion, setEditingPsychotestQuestion] = useState(null);
  const [psychotestExcelFile, setPsychotestExcelFile] = useState(null);
  const [psychotestPage, setPsychotestPage] = useState(1);
  const [psychotestPerPage, setPsychotestPerPage] = useState('10');
  const [selectedPsychotestQuestionIds, setSelectedPsychotestQuestionIds] = useState([]);
  const [selectAllPsychotestQuestions, setSelectAllPsychotestQuestions] = useState(false);
  const [psychotestDurationMinutes, setPsychotestDurationMinutes] = useState(30);
  const [psychotestTotalQuestions, setPsychotestTotalQuestions] = useState(0);
  const [psychotestMaxViolations, setPsychotestMaxViolations] = useState(3);
  const [savingPsychotestDuration, setSavingPsychotestDuration] = useState(false);
  const [psychotestForm, setPsychotestForm] = useState({
    dimension_a: 'extro_intro',
    statement_a: '',
    dimension_b: 'emotional_stability',
    statement_b: '',
    is_active: 1,
  });

  // Schedule management
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleModules, setScheduleModules] = useState([]);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [editingModuleIndex, setEditingModuleIndex] = useState(null);
  const [editingModuleTime, setEditingModuleTime] = useState({
    starts_at: '',
    ends_at: ''
  });

  const PSY_DIMENSIONS = [
  { value: 'Hard Intense Worked', label: 'Hard Intense Worked' },
  { value: 'Need to Achieve', label: 'Need to Achieve' },
  { value: 'Need to Control Others', label: 'Need to Control Others' },
  { value: 'Need to Be Noticed', label: 'Need to Be Noticed' },
  { value: 'Need to Belong to Groups', label: 'Need to Belong to Groups' },
  { value: 'Need for Closeness and Affection', label: 'Need for Closeness and Affection' },
  { value: 'Need for Change', label: 'Need for Change' },
  { value: 'Need to Be Forceful', label: 'Need to Be Forceful' },
  { value: 'Need to Support Authority', label: 'Need to Support Authority' },
  { value: 'Need for Rules and Supervision', label: 'Need for Rules and Supervision' },
  { value: 'Leadership Role', label: 'Leadership Role' },
  { value: 'Ease in Decision Making', label: 'Ease in Decision Making' },
  { value: 'Pace', label: 'Pace' },
  { value: 'Vigorous Type', label: 'Vigorous Type' },
  { value: 'Social Extention', label: 'Social Extention' },
  { value: 'Theoretical Type', label: 'Theoretical Type' },
  { value: 'Interest in Working with Details', label: 'Interest in Working with Details' },
  { value: 'Organized Type', label: 'Organized Type' },
  { value: 'Emotional Resistant', label: 'Emotional Resistant' },
  { value: 'Need to Finish Task', label: 'Need to Finish Task' },
];

  const uploadsBaseUrl = (() => {
    const apiBase = String(process.env.REACT_APP_API_URL || '').trim();
    if (!apiBase || apiBase.startsWith('/')) return '';
    return apiBase.replace(/\/?api\/?$/i, '');
  })();

  const buildUploadUrl = (folder, fileName) => {
    if (!fileName) return '';
    const value = String(fileName);
    if (/^https?:\/\//i.test(value) || value.startsWith('/')) {
      return value;
    }
    const relativePath = `/uploads/${folder}/${value}`;
    return uploadsBaseUrl ? `${uploadsBaseUrl}${relativePath}` : relativePath;
  };

  const parsePsychotestResult = (result) => {
    if (!result) return null;
    if (typeof result === 'string') {
      try {
        return JSON.parse(result);
      } catch (e) {
        return null;
      }
    }
    return result;
  };

  const formatPsychotestResult = (result) => {
  const parsed = parsePsychotestResult(result);
  if (!parsed) {
    return { summary: '-', dimensions: [], groups: [] };
  }

  if (parsed.status === 'disqualified') {
    return {
      summary: `Diskualifikasi${parsed.reason ? ` - ${parsed.reason}` : ''}`,
      dimensions: [],
      groups: [],
    };
  }

  const dimensions = Array.isArray(parsed.dimensions) ? parsed.dimensions : [];
  const groups = Array.isArray(parsed.groups) ? parsed.groups : []; 

  const strongest = parsed.summary?.strongest_dimension || dimensions.slice().sort((a, b) => (b.score || 0) - (a.score || 0))[0];
  const weakest = parsed.summary?.needs_attention_dimension || dimensions.slice().sort((a, b) => (a.score || 0) - (b.score || 0))[0];
  const parts = [];

  if (strongest) {
    parts.push(`Terkuat: ${strongest.title || strongest.key || '-'}${strongest.score != null ? ` (Skor: ${strongest.score}/9)` : ''}`);
  }
  if (weakest) {
    parts.push(`Perlu perhatian: ${weakest.title || weakest.key || '-'}${weakest.score != null ? ` (Skor: ${weakest.score}/9)` : ''}`);
  }

  return {
    summary: parts.length > 0 ? parts.join(' | ') : 'Hasil tersedia',
    dimensions,
    groups,   
  };
};

  // ─── Load Sessions ──────────────────────────────────────────────────────────
  useEffect(() => {
    const loadSessions = async () => {
      try {
        const res = await API.get('/recruiter/sessions');
        setSessions(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingSessions(false);
      }
    };
    loadSessions();
  }, []);


useEffect(() => {
  if (!showCandidateForm || editingCandidateId) return; 
  const filteredExams = candidateFormExams.filter(
    (e) => e.position === 'all' || e.position === candidateForm.position
  );
  setCandidateScheduleModules(createDefaultCandidateSchedule(filteredExams));
}, [candidateForm.position, candidateFormExams, showCandidateForm, editingCandidateId]);

  // ─── Load Exams ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'exams') return;
    const loadExams = async () => {
      setLoadingExams(true);
      try {
        const res = await API.get('/recruiter/exams');
        setExams(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingExams(false);
      }
    };
    loadExams();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'psychotest') return;
    const loadPsychotest = async () => {
      setLoadingPsychotest(true);
      try {
        const [qRes, settingsRes] = await Promise.all([
          API.get('/recruiter/psychotest/questions'),
          API.get('/recruiter/psychotest/settings'),
        ]);
        setPsychotestQuestions(qRes.data);
        setPsychotestDurationMinutes(Number(settingsRes.data?.duration_minutes || 30));
        setPsychotestTotalQuestions(Number(settingsRes.data?.total_questions || 0));
        setPsychotestMaxViolations(Number(settingsRes.data?.max_violations || 3));
        setPsychotestPage(1);
        setSelectedPsychotestQuestionIds([]);
        setSelectAllPsychotestQuestions(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingPsychotest(false);
      }
    };
    loadPsychotest();
  }, [activeTab]);

  useEffect(() => {
    if (user?.role !== 'admin' || activeTab !== 'recruiters') return;
    const loadRecruiters = async () => {
      setLoadingRecruiters(true);
      try {
        const res = await API.get('/admin/recruiters');
        setRecruiters(res.data || []);
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Gagal memuat data recruiter' });
      } finally {
        setLoadingRecruiters(false);
      }
    };
    loadRecruiters();
  }, [activeTab, user?.role]);

    useEffect(() => {
  const loadPositions = async () => {
    setLoadingPositions(true);
    try {
      const res = await API.get('/posisi');
      setPositions(res.data || []);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Gagal memuat data' });
    } finally {
      setLoadingPositions(false);
    }
  };

  loadPositions();
}, [activeTab, user?.role]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ─── Load Questions ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedExamForQuestions) return;
    const loadQuestions = async () => {
      setLoadingQuestions(true);
      try {
        const res = await API.get(`/recruiter/exam/${selectedExamForQuestions}/questions`);
        setQuestions(res.data);
        setQuestionPage(1); 
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingQuestions(false);
      }
    };
    loadQuestions();
  }, [selectedExamForQuestions]);

  useEffect(() => {
    setQuestionPage(1);
  }, [questionPerPage]);

  useEffect(() => {
    setPsychotestPage(1);
  }, [psychotestPerPage]);

  useEffect(() => {
    setCandidatePage(1);
  }, [candidatePerPage, candidateSearch, filter]);

  useEffect(() => {
    setDetailExamPage(1);
  }, [detailExamPerPage, candidateDetail]);

  useEffect(() => {
  if (!psychotestDetail?.id) return undefined;

  const interval = setInterval(async () => {
    try {
      const res = await API.get(
        `/recruiter/psychotest/attempt/${psychotestDetail.id}`
      );

      setPsychotestDetail({
        ...res.data.attempt,
        violations: res.data.violations || [],
        answers: res.data.answers || [],
      });
    } catch (error) {
      console.error('Refresh psychotest detail error:', error);
    }
  }, 3000);

  return () => clearInterval(interval);
}, [psychotestDetail?.id]);

  // ─── Pagination ─────────────────────────────────────────────────────────────
  const resolvePageSize = (value, total) => (value === 'all' ? Math.max(total, 1) : Number(value));

  const effectiveQuestionPerPage = resolvePageSize(questionPerPage, questions.length);
  const totalPages = Math.max(1, Math.ceil(questions.length / effectiveQuestionPerPage));
  const paginatedQuestions = questions.slice(
    (questionPage - 1) * effectiveQuestionPerPage,
    questionPage * effectiveQuestionPerPage
  );

  // ─── Session Detail ─────────────────────────────────────────────────────────
  // const viewDetail = async (sess) => {
  //   if (!sess?.id) {
  //     Swal.fire({ icon: 'info', title: 'Info', text: 'Peserta ini belum memiliki sesi ujian.' });
  //     return;
  //   }
  //   setSelected(sess);
  //   setDetail(null);
  //   setDetailLoading(true);
  //   try {
  //     const res = await API.get(`/recruiter/session/${sess.id}`);
  //     setDetail(res.data);
  //   } catch (e) {}
  //   setDetailLoading(false);
  // };

  // const openCandidateDetail = async (sess) => {
  //   setCandidateDetailLoading(true);
  //   setCandidateDetail(null);
  //   setDetailExamPage(1);
  //   setShowCandidateDetailModal(true);
  //   try {
  //     const res = await API.get(`/recruiter/candidate/${sess.candidate_id}`);
  //     setCandidateDetail(res.data);
  //     setEditingCandidateId(sess.candidate_id);
  //   } catch (err) {
  //     console.error(err);
  //     Swal.fire({ icon: 'error', title: 'Error', text: 'Gagal memuat detail kandidat' });
  //     setShowCandidateDetailModal(false);
  //   } finally {
  //     setCandidateDetailLoading(false);
  //   }
  // };

  const openCandidateDetail = async (sess) => {
  setCandidateDetailLoading(true);
  setCandidateDetail(null);
  setShowProfileSection(true);
  setDetailExamPage(1);
  setShowCandidateDetailModal(true);
  try {
    const res = await API.get(`/recruiter/candidate/${sess.candidate_id}`);
    setCandidateDetail(res.data);
    setEditingCandidateId(sess.candidate_id);
  } catch (err) {
    console.error(err);
    Swal.fire({ icon: 'error', title: 'Error', text: 'Gagal memuat detail kandidat' });
    setShowCandidateDetailModal(false);
  } finally {
    setCandidateDetailLoading(false);
  }
};

  const openSessionAnswerDetail = async (examSession) => {
    if (!examSession?.session_id) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Detail jawaban belum tersedia untuk sesi ini.' });
      return;
    }

    setSessionDetailLoading(true);
    setSessionDetail(null);
    setShowSessionDetailModal(true);
    try {
      const res = await API.get(`/recruiter/session/${examSession.session_id}`);
      setSessionDetail(res.data);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Gagal memuat detail jawaban ujian' });
      setShowSessionDetailModal(false);
    } finally {
      setSessionDetailLoading(false);
      setSelectedViolations(new Set());
    }
  };


const openPsychotestAttemptDetail = async (attempt) => {
  setShowPsychotestDetailModal(true);
  setPsychotestDetail(null);
  try {
    const res = await API.get(`/recruiter/psychotest/attempt/${attempt.id}`);
    setPsychotestDetail({
      ...res.data.attempt,
      violations: res.data.violations || [],
      answers: res.data.answers || [],
    });
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: 'Gagal memuat detail psikotest' });
    setShowPsychotestDetailModal(false);
  }
};

  const closePsychotestDetailModal = () => {
    setShowPsychotestDetailModal(false);
    setPsychotestDetail(null);
    setSelectedViolations(new Set());
  };

  // ─── Schedule Management ───────────────────────────────────────────────────
//   const openScheduleManager = () => {
//     if (!candidateDetail?.schedule_modules) {
//       Swal.fire({ icon: 'info', title: 'Info', text: 'Data jadwal belum tersedia' });
//       return;
//     }
    
//   const modules = Array.isArray(candidateDetail.schedule_modules)
//   ? candidateDetail.schedule_modules.map(m => ({
//       ...m,
//       is_active: m.is_active == null ? 1 : Number(m.is_active)
//     }))
//   : [];

// setCandidateScheduleModules(modules);
//     setEditingModuleIndex(null);
//     setEditingModuleTime({ starts_at: '', ends_at: '' });
//     setShowScheduleModal(true);
//   };

  const closeScheduleModal = () => {
    setShowScheduleModal(false);
    setScheduleModules([]);
    setEditingModuleIndex(null);
    setEditingModuleTime({ starts_at: '', ends_at: '' });
  };

  const canEditSchedule = (module) => {
    return !['active', 'completed', 'failed', 'disqualified'].includes(module?.module_status);
  };

  const moveModuleUp = (index) => {
    if (index <= 0) return;
    
    const newModules = [...scheduleModules];
    [newModules[index - 1].sequence_no, newModules[index].sequence_no] = 
      [newModules[index].sequence_no, newModules[index - 1].sequence_no];
    
    newModules.sort((a, b) => a.sequence_no - b.sequence_no);
    setScheduleModules(newModules);
  };

  const moveModuleDown = (index) => {
    if (index >= scheduleModules.length - 1) return;
    
    const newModules = [...scheduleModules];
    [newModules[index].sequence_no, newModules[index + 1].sequence_no] = 
      [newModules[index + 1].sequence_no, newModules[index].sequence_no];
    
    newModules.sort((a, b) => a.sequence_no - b.sequence_no);
    setScheduleModules(newModules);
  };

  const startEditModuleTime = (index) => {
    const module = scheduleModules[index];
    if (!canEditSchedule(module)) {
      Swal.fire({ 
        icon: 'warning', 
        title: 'Tidak Bisa Diedit', 
        text: `Modul ini sudah ${module.module_status}. Tidak bisa diedit.` 
      });
      return;
    }

    setEditingModuleIndex(index);
    setEditingModuleTime({
      starts_at: module.starts_at ? module.starts_at.slice(0, 16) : '',
      ends_at: module.ends_at ? module.ends_at.slice(0, 16) : ''
    });
  };

  const cancelEditModuleTime = () => {
    setEditingModuleIndex(null);
    setEditingModuleTime({ starts_at: '', ends_at: '' });
  };

  const saveModuleTime = () => {
    if (!editingModuleIndex === undefined) return;

    const { starts_at, ends_at } = editingModuleTime;
    
    if (starts_at && ends_at && new Date(starts_at) >= new Date(ends_at)) {
      Swal.fire({ 
        icon: 'warning', 
        title: 'Waktu Tidak Valid', 
        text: 'Waktu selesai harus lebih besar dari waktu mulai' 
      });
      return;
    }

    const newModules = [...scheduleModules];
    newModules[editingModuleIndex] = {
      ...newModules[editingModuleIndex],
      starts_at: starts_at || null,
      ends_at: ends_at || null
    };
    setScheduleModules(newModules);
    cancelEditModuleTime();
  };

  const saveScheduleChanges = async () => {
    if (!candidateDetail?.candidate?.id) return;
    setSavingSchedule(true);
    try {
      const payload = {
        modules: scheduleModules.map(m => ({
          module_type: m.module_type,
          exam_id: m.exam_id,
          sequence_no: m.sequence_no,
          starts_at: m.starts_at,
          ends_at: m.ends_at,
          auto_open: m.auto_open === 0 || m.auto_open === false ? 0 : 1
        }))
      };

      await API.put(`/recruiter/candidate/${candidateDetail.candidate.id}/schedules`, payload);
      Swal.fire({ 
        icon: 'success', 
        title: 'Berhasil', 
        text: 'Jadwal peserta berhasil diperbarui', 
        timer: 1500, 
        showConfirmButton: false 
      });

      const [detailRes, sessionsRes] = await Promise.all([
        API.get(`/recruiter/candidate/${candidateDetail.candidate.id}`),
        API.get('/recruiter/sessions'),
      ]);
      setCandidateDetail(detailRes.data);
      setSessions(sessionsRes.data);
      closeScheduleModal();
    } catch (err) {
      Swal.fire({ 
        icon: 'error', 
        title: 'Error', 
        text: err.response?.data?.message || err.message 
      });
    } finally {
      setSavingSchedule(false);
    }
  };

  const resetCandidateExamData = async () => {
  if (!candidateDetail?.candidate?.id) {
    Swal.fire({ icon: 'info', title: 'Info', text: 'Detail kandidat belum tersedia' });
    return;
  }

  if (selectedExams.length === 0) {
    Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih minimal satu ujian yang ingin direset' });
    return;
  }

  const result = await Swal.fire({
    icon: 'warning',
    title: `Reset ${selectedExams.length} Ujian Terpilih?`,
    text: 'Riwayat ujian, jawaban, foto sesi, dan pelanggaran untuk ujian yang dipilih akan dihapus. Kandidat bisa test ulang ujian ini.',
    showCancelButton: true,
    confirmButtonText: 'Ya, Reset',
    cancelButtonText: 'Batal',
    confirmButtonColor: '#ef4444',
  });

  if (!result.isConfirmed) return;

  try {
    await API.post(`/recruiter/candidate/${candidateDetail.candidate.id}/reset-exam`, {
      session_ids: selectedExams,
    });
    Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Data ujian terpilih berhasil direset', timer: 1500, showConfirmButton: false });
    setSelectedExams([]);
    const [detailRes, sessionsRes] = await Promise.all([
      API.get(`/recruiter/candidate/${candidateDetail.candidate.id}`),
      API.get('/recruiter/sessions'),
    ]);
    setCandidateDetail(detailRes.data);
    setSessions(sessionsRes.data);
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
  }
};

  const resetCandidatePsychotestData = async () => {
    if (!candidateDetail?.candidate?.id) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Detail kandidat belum tersedia' });
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Reset Data Psikotest?',
      text: 'Status psikotest, hasil, riwayat attempt, dan foto awal psikotest kandidat ini akan dihapus. Kandidat bisa test ulang.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Reset Psikotest',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });

    if (!result.isConfirmed) return;

    try {
      await API.post(`/recruiter/candidate/${candidateDetail.candidate.id}/reset-psychotest`);
      Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Data psikotest kandidat berhasil direset', timer: 1500, showConfirmButton: false });
      const [detailRes, sessionsRes] = await Promise.all([
        API.get(`/recruiter/candidate/${candidateDetail.candidate.id}`),
        API.get('/recruiter/sessions'),
      ]);
      setCandidateDetail(detailRes.data);
      setSessions(sessionsRes.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const openExamQuestionsModal = (exam) => {
    setExamModalExam(exam);
    setShowExamQuestionsModal(true);
    setQuestionPage(1);
    if (selectedExamForQuestions !== exam.id) {
      setSelectedExamForQuestions(exam.id);
    }
  };

  const downloadExcel = (rows, filename, sheetName = 'Data') => {
    if (!rows || rows.length === 0) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Tidak ada data untuk di-export' });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    const dateSuffix = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `${filename}_${dateSuffix}.xlsx`);
  };

  const exportCandidatesExcel = () => {
    const rows = paginatedCandidates.map((s, idx) => {
      const overallStatus = s.overall_status || 'in_progress';
const scoreValue = Number(s.score || 0);
const badgeClass =
  overallStatus === 'lulus' ? 'pass'
  : overallStatus === 'tidak_lulus' ? 'fail'
  : overallStatus === 'selesai_tes' ? 'selesai'
  : overallStatus === 'expired' ? 'expired'
  : 'active';
      return {
        No: ((candidatePage - 1) * effectiveCandidatePerPage) + idx + 1,
        Nama: s.candidate_name || '-',
        Email: s.candidate_email || '-',
        Posisi: s.position || '-',
        Status: status,
        Nilai: overallStatus === 'in_progress' ? '-' : `${scoreValue}%`,
        Pelanggaran: s.violation_count || 0,
        Email_Hasil: s.result_email_sent_at ? 'Terkirim' : 'Belum',
      };
    });
    downloadExcel(rows, 'peserta_rekrutmen', 'Peserta');
  };

const sendCandidateResultEmail = async (candidate, status) => {
  const candidateId = candidate?.candidate_id;
  if (!candidateId) return;


  const titleMap = {
    lulus: 'Kirim Email LULUS?',
    tidak_lulus: 'Kirim Email TIDAK LULUS?',
    lulus_dengan_undangan: 'Kirim Email LULUS + Undangan Tahap Berikutnya?',
  };

  const textMap = {
    lulus: `Email kelulusan akan dikirim ke ${candidate.candidate_email}.`,
    tidak_lulus: `Email tidak lulus akan dikirim ke ${candidate.candidate_email}.`,
    lulus_dengan_undangan: `Email kelulusan beserta undangan tes tahap berikutnya akan dikirim ke ${candidate.candidate_email}. Jadwal diambil dari jadwal tes kandidat yang sudah diatur.`,
  };

  const confirmResult = await Swal.fire({
    icon: 'question',
    title: titleMap[status] || `Kirim Email ${status}?`,
    text: textMap[status] || `Email akan dikirim ke ${candidate.candidate_email}.`,
    showCancelButton: true,
    confirmButtonText: 'Ya, Kirim',
    cancelButtonText: 'Batal',
  });
  if (!confirmResult.isConfirmed) return;

  try {
    const res = await API.post(`/recruiter/candidate/${candidateId}/send-result-email`, { status });
    Swal.fire({ icon: 'success', title: 'Email Terkirim', text: res.data?.message, timer: 1800, showConfirmButton: false });
    const sessionsRes = await API.get('/recruiter/sessions');
    setSessions(sessionsRes.data);
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
  }
};

  const exportCandidateExamHistoryExcel = () => {
    const rows = paginatedCandidateDetailExams.map((exam, idx) => ({
      No: ((detailExamPage - 1) * effectiveDetailExamPerPage) + idx + 1,
      Ujian: exam.exam_title || '-',
      Status: exam.is_disqualified ? 'Diskualifikasi' : exam.status === 'active' ? 'Sedang Ujian' : exam.status === 'finished' ? 'Selesai' : exam.status,
      Nilai: exam.score != null ? `${exam.score}%` : '-',
      Pelanggaran: exam.violation_count || 0,
      Foto: exam.session_photo ? 'Ada' : '-',
      Waktu: exam.started_at ? new Date(exam.started_at).toLocaleString('id-ID') : '-'
    }));
    downloadExcel(rows, 'riwayat_ujian_kandidat', 'Riwayat Ujian');
  };

  const exportExamQuestionsExcel = () => {
    const rows = paginatedQuestions.map((q, idx) => ({
      No: ((questionPage - 1) * effectiveQuestionPerPage) + idx + 1,
      Pertanyaan: q.question_text || '-',
      Tipe: q.correct_answer ? 'Pilihan Ganda' : 'Essay',
      Opsi_A: q.option_a || '-',
      Opsi_B: q.option_b || '-',
      Opsi_C: q.option_c || '-',
      Opsi_D: q.option_d || '-',
      Opsi_E: q.option_e || '-',
      Jawaban_Benar: q.correct_answer || '-',
      Rubrik: !q.correct_answer ? (q.option_a || '-') : '-',
      Poin: q.points || 0
    }));
    downloadExcel(rows, 'bank_soal_ujian', 'Soal Ujian');
  };

  const exportPsychotestExcel = () => {
    const rows = paginatedPsychotestQuestions.map((q) => ({
      ID: q.id,
      Dimensi_A: PSY_DIMENSIONS.find((d) => d.value === q.dimension_a)?.label || q.dimension_a,
      Pernyataan_A: q.statement_a || '-',
      Dimensi_B: PSY_DIMENSIONS.find((d) => d.value === q.dimension_b)?.label || q.dimension_b,
      Pernyataan_B: q.statement_b || '-',
      Status: Number(q.is_active) === 1 ? 'Aktif' : 'Nonaktif'
    }));
    downloadExcel(rows, 'soal_psikotest', 'Psikotest');
  };

  const downloadCandidateDetailPdf = () => {
    if (!candidateDetail) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Detail kandidat belum tersedia' });
      return;
    }

    const doc = new jsPDF();
    const c = candidateDetail.candidate || {};
    const exams = candidateDetail.exams || [];
    const psycho = candidateDetail.psychotest_attempts || [];
    const documentTitle = 'Detail Kandidat';
    const psychotestSummary = formatPsychotestResult(c.psychotest_result || psycho.find((attempt) => attempt.status === 'completed' && attempt.result)?.result);
    const generatedAt = new Date().toLocaleString('id-ID');

    let cursorY = drawPdfHeader(doc, {
      title: documentTitle,
      subtitle: c.name || 'Ringkasan data kandidat',
      kicker: 'Detail kandidat',
    });

    cursorY = drawPdfInfoTable(doc, cursorY, [
      ['Nama', c.name || '-'],
      ['Email', c.email || '-'],
      ['Posisi', c.position || '-'],
      ['Psikotest', c.psychotest_completed ? 'Selesai' : 'Belum'],
      ['Ringkasan Psikotest', psychotestSummary.summary],
      ['Dicetak Pada', generatedAt],
    ]);

    cursorY += 10;
    cursorY = ensurePdfSpace(doc, cursorY, 55, documentTitle);
    cursorY = drawPdfSectionTitle(doc, `Riwayat Ujian (${exams.length})`, cursorY);

    if (exams.length > 0) {
      cursorY = drawPdfTable(doc, {
        startY: cursorY,
        title: documentTitle,
        head: [['No', 'Ujian', 'Foto', 'Status', 'Nilai', 'Pelanggaran', 'Waktu']],
        body: exams.map((exam, idx) => ([
          idx + 1,
          exam.exam_title || '-',
          exam.session_photo ? 'Ada' : '-',
          exam.is_disqualified ? 'Diskualifikasi' : exam.status === 'active' ? 'Sedang Ujian' : exam.status === 'finished' ? 'Selesai' : exam.status,
          exam.score != null ? `${exam.score}%` : '-',
          exam.violation_count || 0,
          exam.started_at ? new Date(exam.started_at).toLocaleString('id-ID') : '-',
        ])),
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          2: { cellWidth: 14, halign: 'center' },
          3: { cellWidth: 24, halign: 'center' },
          4: { cellWidth: 16, halign: 'center' },
          5: { cellWidth: 22, halign: 'center' },
          6: { cellWidth: 40 },
        },
      });
    } else {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9.5);
      doc.setTextColor(...PDF_MUTED);
      doc.text('Tidak ada riwayat ujian.', PDF_MARGIN, cursorY + 4);
      doc.setTextColor(...PDF_TEXT);
      cursorY += 10;
    }

    cursorY += 10;
    cursorY = ensurePdfSpace(doc, cursorY, 55, documentTitle);
    cursorY = drawPdfSectionTitle(doc, `Riwayat Psikotest (${psycho.length})`, cursorY);

    if (psycho.length > 0) {
      cursorY = drawPdfTable(doc, {
        startY: cursorY,
        title: documentTitle,
        head: [['No', 'Status', 'Mulai', 'Selesai', 'Hasil']],
        body: psycho.map((attempt, idx) => ([
          idx + 1,
          attempt.status === 'completed' ? 'Selesai' : attempt.status === 'active' ? 'Sedang Berlangsung' : attempt.status,
          attempt.started_at ? new Date(attempt.started_at).toLocaleString('id-ID') : '-',
          attempt.completed_at ? new Date(attempt.completed_at).toLocaleString('id-ID') : '-',
          attempt.status === 'completed' ? formatPsychotestResult(attempt.result).summary : (attempt.result ? 'Hasil tersedia' : 'Belum ada hasil'),
        ])),
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 30, halign: 'center' },
          2: { cellWidth: 36 },
          3: { cellWidth: 36 },
        },
      });
    } else {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9.5);
      doc.setTextColor(...PDF_MUTED);
      doc.text('Tidak ada riwayat psikotest.', PDF_MARGIN, cursorY + 4);
      doc.setTextColor(...PDF_TEXT);
      cursorY += 10;
    }

    drawPdfFooter(doc);

    const safeName = (c.name || 'kandidat').replace(/[^a-zA-Z0-9-_]/g, '_');
    doc.save(`detail_kandidat_${safeName}.pdf`);
  };

  const downloadSessionDetailPdf = () => {
    if (!sessionDetail) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Detail jawaban belum tersedia' });
      return;
    }

    const doc = new jsPDF();
    const session = sessionDetail.session || {};
    const answers = sessionDetail.answers || [];
    const violations = sessionDetail.violations || [];

    const statusLabel = session.is_disqualified
      ? 'Diskualifikasi'
      : session.status === 'active'
        ? 'Sedang Ujian'
        : session.status === 'finished'
          ? 'Selesai'
          : session.status || '-';

    const multipleChoiceAnswers = answers.filter((answer) => answer.correct_answer);
    const correctCount = multipleChoiceAnswers.filter((answer) => Number(answer.is_correct) === 1).length;
    const wrongCount = multipleChoiceAnswers.length - correctCount;
    const essayCount = answers.length - multipleChoiceAnswers.length;
    const generatedAt = new Date().toLocaleString('id-ID');
    const documentTitle = 'Hasil Ujian (Exam)';

    let cursorY = drawPdfHeader(doc, {
      title: documentTitle,
      subtitle: session.exam_title || 'Rekap jawaban dan penilaian kandidat',
      kicker: 'Hasil exam',
    });

    cursorY = drawPdfInfoTable(doc, cursorY, [
      ['Nama Kandidat', session.candidate_name || '-'],
      ['Email', session.candidate_email || '-'],
      ['Ujian', session.exam_title || '-'],
      ['Status', statusLabel],
      ['Skor Akhir', session.score != null ? `${session.score}%` : '-'],
      ['Foto Sesi', session.photo ? 'Tersedia' : 'Tidak ada'],
      ['Dicetak Pada', generatedAt],
    ]);

    cursorY += 10;
    cursorY = ensurePdfSpace(doc, cursorY, 60, documentTitle);
    cursorY = drawPdfSectionTitle(doc, 'Ringkasan Penilaian', cursorY);
    cursorY = drawPdfTable(doc, {
      startY: cursorY,
      title: documentTitle,
      head: [['Total Soal', 'Pilihan Ganda', 'Jawaban Benar', 'Jawaban Salah', 'Essay']],
      body: [[answers.length, multipleChoiceAnswers.length, correctCount, wrongCount, essayCount]],
      columnStyles: {
        0: { halign: 'center' },
        1: { halign: 'center' },
        2: { halign: 'center', textColor: [21, 128, 61], fontStyle: 'bold' },
        3: { halign: 'center', textColor: [185, 28, 28], fontStyle: 'bold' },
        4: { halign: 'center' },
      },
    });

    cursorY += 10;
    cursorY = ensurePdfSpace(doc, cursorY, 60, documentTitle);
    cursorY = drawPdfSectionTitle(doc, `Rincian Jawaban (${answers.length})`, cursorY);

    if (answers.length > 0) {
      cursorY = drawPdfTable(doc, {
        startY: cursorY,
        title: documentTitle,
        head: [['No', 'Pertanyaan', 'Foto', 'Jawaban Kandidat', 'Kunci', 'Status']],
        body: answers.map((answer, idx) => ([
          idx + 1,
          answer.question_text || '-',
          answer.photo ? 'Ada' : '-',
          answer.correct_answer ? String(answer.selected_option || '-').toUpperCase() : (answer.selected_option || '-'),
          answer.correct_answer ? String(answer.correct_answer).toUpperCase() : '-',
          answer.correct_answer ? (Number(answer.is_correct) === 1 ? 'Benar' : 'Salah') : 'Essay',
        ])),
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          2: { cellWidth: 14, halign: 'center' },
          3: { cellWidth: 28, halign: 'center' },
          4: { cellWidth: 16, halign: 'center' },
          5: { cellWidth: 18, halign: 'center' },
        },
      });
    } else {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9.5);
      doc.setTextColor(...PDF_MUTED);
      doc.text('Tidak ada jawaban yang tersimpan.', PDF_MARGIN, cursorY + 4);
      doc.setTextColor(...PDF_TEXT);
      cursorY += 10;
    }

    if (violations.length > 0) {
      cursorY += 6;
      cursorY = ensurePdfSpace(doc, cursorY, 55, documentTitle);
      cursorY = drawPdfSectionTitle(doc, `Pelanggaran (${violations.length})`, cursorY);
      cursorY = drawPdfTable(doc, {
        startY: cursorY,
        title: documentTitle,
        head: [['No', 'Jenis Pelanggaran', 'Deskripsi', 'Waktu']],
        body: violations.map((violation, idx) => ([
          idx + 1,
          VIOLATION_LABELS[violation.violation_type] || violation.violation_type || '-',
          violation.description || '-',
          violation.logged_at ? new Date(violation.logged_at).toLocaleString('id-ID') : '-',
        ])),
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 40 },
          3: { cellWidth: 34 },
        },
      });
    }

    drawPdfFooter(doc);

    const safeName = (session.candidate_name || 'sesi_ujian').replace(/[^a-zA-Z0-9-_]/g, '_');
    doc.save(`hasil_exam_${safeName}.pdf`);
  };

  const downloadPsychotestDetailPdf = () => {
    if (!psychotestDetail) {
      Swal.fire({ icon: 'info', title: 'Info', text: 'Detail psikotest belum tersedia' });
      return;
    }

    const doc = new jsPDF();
    const documentTitle = 'Hasil Psikotest';

    const statusLabel = psychotestDetail.status === 'completed'
      ? 'Selesai'
      : psychotestDetail.status === 'active'
        ? 'Sedang Berlangsung'
        : psychotestDetail.status === 'timeout'
          ? 'Timeout'
          : psychotestDetail.status === 'disqualified'
            ? 'Diskualifikasi'
            : psychotestDetail.status || '-';

    const formattedResult = formatPsychotestResult(psychotestDetail.result);
    const resultSummary = psychotestDetail.result ? formattedResult.summary : '-';
    const dimensions = Array.isArray(formattedResult.dimensions) ? formattedResult.dimensions : [];
    const groupOrder = {
      'HIGH ANALISYS': 0,
      'MIDDLE RANGE': 1,
      'LOW ANALISYS': 2,
    };
    const groups = [...(formattedResult.groups || [])].sort(
      (a, b) => (groupOrder[a.label] ?? 3) - (groupOrder[b.label] ?? 3)
    );

    const candidateName = candidateDetail?.candidate?.name || psychotestDetail?.candidate_name || '-';
    const safeCandidateName = candidateName.replace(/[^a-zA-Z0-9-_]/g, '_');
    const generatedAt = new Date().toLocaleString('id-ID');
    const answers = Array.isArray(psychotestDetail.answers) ? psychotestDetail.answers : [];
    const violations = Array.isArray(psychotestDetail.violations) ? psychotestDetail.violations : [];

    let cursorY = drawPdfHeader(doc, {
      title: documentTitle,
      subtitle: candidateName !== '-' ? candidateName : 'Rekap kecenderungan dan jawaban kandidat',
      kicker: 'Hasil psikotest',
    });

    cursorY = drawPdfInfoTable(doc, cursorY, [
      ['Nama Kandidat', candidateName],
      ['Status', statusLabel],
      ['Mulai', psychotestDetail.started_at ? new Date(psychotestDetail.started_at).toLocaleString('id-ID') : '-'],
      ['Selesai', psychotestDetail.completed_at ? new Date(psychotestDetail.completed_at).toLocaleString('id-ID') : '-'],
      ['Ringkasan', resultSummary],
      ['Dicetak Pada', generatedAt],
    ]);

    if (groups.length > 0) {
      cursorY += 10;
      cursorY = ensurePdfSpace(doc, cursorY, 55, documentTitle);
      cursorY = drawPdfSectionTitle(doc, 'Ringkasan Kecenderungan per Kelompok', cursorY);
      cursorY = drawPdfTable(doc, {
        startY: cursorY,
        title: documentTitle,
        head: [['Kelompok', 'Kategori', 'Rata-rata', 'Jumlah Dimensi']],
        body: groups.map((group) => ([
          group.title || '-',
          group.label || '-',
          group.averageScore != null ? group.averageScore : '-',
          Array.isArray(group.members) ? group.members.length : 0,
        ])),
        columnStyles: {
          1: { cellWidth: 38, halign: 'center' },
          2: { cellWidth: 24, halign: 'center' },
          3: { cellWidth: 32, halign: 'center' },
        },
      });

      const dimensionRows = groups.flatMap((group) => (
        (group.members || []).map((member) => ([
          group.title || '-',
          member.title || member.key || '-',
          member.score != null ? `${member.score}/9` : '-',
          member.level ? `${member.level} - ${member.tendency || '-'}` : (member.tendency || '-'),
        ]))
      ));

      if (dimensionRows.length > 0) {
        cursorY += 10;
        cursorY = ensurePdfSpace(doc, cursorY, 55, documentTitle);
        cursorY = drawPdfSectionTitle(doc, 'Detail Skor Dimensi', cursorY);
        cursorY = drawPdfTable(doc, {
          startY: cursorY,
          title: documentTitle,
          head: [['Kelompok', 'Dimensi', 'Skor', 'Kecenderungan']],
          body: dimensionRows,
          columnStyles: {
            0: { cellWidth: 34 },
            1: { cellWidth: 46 },
            2: { cellWidth: 16, halign: 'center' },
          },
        });
      }
    } else if (dimensions.length > 0) {
      cursorY += 10;
      cursorY = ensurePdfSpace(doc, cursorY, 55, documentTitle);
      cursorY = drawPdfSectionTitle(doc, 'Detail Skor Dimensi', cursorY);
      cursorY = drawPdfTable(doc, {
        startY: cursorY,
        title: documentTitle,
        head: [['Dimensi', 'Skor', 'Kecenderungan']],
        body: dimensions.map((dimension) => ([
          dimension.title || dimension.key || '-',
          dimension.score != null ? `${dimension.score}/9` : '-',
          dimension.level ? `${dimension.level} - ${dimension.tendency || '-'}` : (dimension.tendency || '-'),
        ])),
        columnStyles: {
          0: { cellWidth: 56 },
          1: { cellWidth: 16, halign: 'center' },
        },
      });
    }

    cursorY += 10;
    cursorY = ensurePdfSpace(doc, cursorY, 55, documentTitle);
    cursorY = drawPdfSectionTitle(doc, 'Catatan Penilaian', cursorY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...PDF_MUTED);
    const scoringNotes = doc.splitTextToSize(
      'Psikotest tidak menilai benar atau salah. Setiap soal memiliki dua pernyataan (A dan B). '
      + 'Skor tiap dimensi dihitung dari jumlah pilihan pada dimensi tersebut dibagi jumlah kemunculannya, '
      + 'lalu dikalikan 9. Semakin tinggi skor, semakin kuat kecenderungan kepribadian pada dimensi tersebut.',
      doc.internal.pageSize.getWidth() - (PDF_MARGIN * 2)
    );
    doc.text(scoringNotes, PDF_MARGIN, cursorY);
    doc.setTextColor(...PDF_TEXT);
    cursorY += (scoringNotes.length * 4.5) + 8;

    cursorY = ensurePdfSpace(doc, cursorY, 55, documentTitle);
    cursorY = drawPdfSectionTitle(doc, `Detail Jawaban (${answers.length})`, cursorY);

    if (answers.length > 0) {
      cursorY = drawPdfTable(doc, {
        startY: cursorY,
        title: documentTitle,
        head: [['No', 'Dimensi A', 'Pernyataan A', 'Dimensi B', 'Pernyataan B', 'Jawaban']],
        body: answers.map((answer, idx) => ([
          idx + 1,
          answer.dimension_a || '-',
          answer.statement_a || '-',
          answer.dimension_b || '-',
          answer.statement_b || '-',
          String(answer.answer_value || '-').toUpperCase(),
        ])),
        styles: { fontSize: 7.6, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 8, halign: 'center' },
          1: { cellWidth: 26 },
          3: { cellWidth: 26 },
          5: { cellWidth: 12, halign: 'center' },
        },
      });
    } else {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9.5);
      doc.setTextColor(...PDF_MUTED);
      doc.text('Tidak ada detail pertanyaan yang tersimpan.', PDF_MARGIN, cursorY + 4);
      doc.setTextColor(...PDF_TEXT);
      cursorY += 10;
    }

    if (violations.length > 0) {
      cursorY += 6;
      cursorY = ensurePdfSpace(doc, cursorY, 55, documentTitle);
      cursorY = drawPdfSectionTitle(doc, `Pelanggaran (${violations.length})`, cursorY);
      cursorY = drawPdfTable(doc, {
        startY: cursorY,
        title: documentTitle,
        head: [['No', 'Jenis Pelanggaran', 'Deskripsi', 'Waktu']],
        body: violations.map((violation, idx) => ([
          idx + 1,
          VIOLATION_LABELS[violation.violation_type] || violation.violation_type || '-',
          violation.description || '-',
          violation.logged_at ? new Date(violation.logged_at).toLocaleString('id-ID') : '-',
        ])),
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 40 },
          3: { cellWidth: 34 },
        },
      });
    }

    drawPdfFooter(doc);

    doc.save(`hasil_psikotest_${safeCandidateName}_${psychotestDetail.id}.pdf`);
  };

  const downloadDashboardTutorialPdf = async () => {
    try {
      const assets = await getTutorialAssets();
      const doc = new jsPDF();
      const isAdmin = user?.role === 'admin';
      const marginLeft = 14;
      const pageWidth = doc.internal.pageSize.getWidth();
      const contentWidth = pageWidth - (marginLeft * 2);
      let cursorY = 16;

      renderTutorialCoverPage(doc, assets, {
        title: 'Panduan Recruiter Dashboard',
        subtitle: 'Panduan ini menjelaskan fungsi utama dashboard, pengelolaan kandidat, reset data, detail ujian, detail psikotest, dan fitur admin.',
        bullets: [
          'Kelola peserta, ujian, soal, dan psikotest dari satu tempat.',
          'Buka detail kandidat untuk melihat riwayat, foto, dan hasil tes.',
          'Gunakan reset data jika kandidat perlu test ulang.',
          isAdmin ? 'Admin bisa mengelola akun recruiter langsung dari dashboard.' : 'Akses admin akan muncul hanya untuk role admin.',
        ],
        accent: [13, 122, 122],
        kicker: 'Panduan penggunaan dashboard recruiter dan admin',
        footer: 'Dokumen ini dibuat langsung dari halaman Recruiter/Admin.',
      });

      doc.addPage();

      const addSectionTitle = (title) => {
        if (assets?.logoDataUrl) {
          doc.addImage(assets.logoDataUrl, 'PNG', marginLeft, cursorY - 1, 7, 7);
        }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(title, marginLeft + 11, cursorY + 5);
        cursorY += 11;
      };

    const addParagraph = (text, size = 11) => {
      doc.setFontSize(size);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(text, contentWidth);
      doc.text(lines, marginLeft, cursorY);
      cursorY += lines.length * 5 + 2;
    };

    const addBullets = (items) => {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      items.forEach((item) => {
        const lines = doc.splitTextToSize(item, contentWidth - 6);
        doc.text(`• ${lines[0]}`, marginLeft, cursorY);
        if (lines.length > 1) {
          doc.text(lines.slice(1), marginLeft + 6, cursorY + 5);
          cursorY += (lines.length * 5);
        } else {
          cursorY += 5;
        }
      });
      cursorY += 2;
    };

    const ensureSpace = (needed) => {
      if (cursorY + needed > 275) {
        doc.addPage();
        cursorY = 16;
      }
    };

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Panduan Recruiter Dashboard', marginLeft, cursorY);
    cursorY += 8;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Role aktif: ${isAdmin ? 'Admin' : 'Recruiter'}`, marginLeft, cursorY);
    cursorY += 8;

    addSectionTitle('Gambaran Umum');
    addParagraph('Dashboard ini dipakai untuk mengelola peserta, ujian, soal, psikotest, serta recruiter (khusus admin). Semua detail kandidat juga bisa dibuka untuk melihat riwayat ujian, riwayat psikotest, dan melakukan reset data jika perlu test ulang.');

    ensureSpace(60);
    addSectionTitle('Fungsi Utama');
    autoTable(doc, {
      startY: cursorY,
      head: [['Fitur', 'Lokasi', 'Fungsi']],
      body: [
        ['Data Peserta', 'Tab Peserta Ujian', 'Cari peserta, lihat status, buka detail kandidat, dan export data.'],
        ['Detail Kandidat', 'Tombol Detail', 'Lihat riwayat ujian, riwayat psikotest, download PDF detail, reset exam, reset psikotest.'],
        ['Detail Ujian', 'Popup Detail Jawaban', 'Lihat foto sesi, pertanyaan, jawaban kandidat, dan pelanggaran.'],
        ['Kelola Ujian', 'Tab Kelola Ujian & Soal', 'Tambah, edit, hapus ujian dan bank soal, upload Excel, export Excel.'],
        ['Kelola Psikotest', 'Tab Kelola Psikotest', 'Tambah, edit, hapus soal psikotest, atur durasi, upload Excel, export Excel.'],
        [isAdmin ? 'Kelola Recruiter' : 'Akses Recruiter', 'Tab Admin', isAdmin ? 'Tambah, edit, hapus recruiter.' : 'Tab ini hanya muncul untuk admin.'],
      ].filter(Boolean),
      styles: { fontSize: 9, cellPadding: 3, valign: 'top' },
      headStyles: { fillColor: [13, 122, 122] },
      margin: { left: marginLeft, right: marginLeft },
    });
    cursorY = doc.lastAutoTable.finalY + 8;

    ensureSpace(70);
    addSectionTitle('Alur Singkat Pemakaian');
    addBullets([
      'Buka tab Peserta Ujian untuk mencari kandidat berdasarkan nama, email, atau posisi.',
      'Klik Detail pada kandidat untuk melihat riwayat ujian dan psikotest.',
      'Gunakan tombol Reset Exam atau Reset Psikotest jika kandidat perlu test ulang.',
      'Masuk ke tab Kelola Ujian & Soal untuk menambah ujian, upload soal Excel, atau export bank soal.',
      'Masuk ke tab Kelola Psikotest untuk mengatur durasi, jumlah soal, dan bank soal A/B.',
      isAdmin ? 'Jika Anda admin, buka tab Kelola Recruiter untuk mengelola akun recruiter.' : 'Jika Anda bukan admin, tab Kelola Recruiter tidak akan ditampilkan.',
    ]);

    ensureSpace(70);
    addSectionTitle('Cara Penilaian Psikotest');
    addParagraph('Penilaian psikotest memakai kecenderungan, bukan benar/salah. Setiap soal punya dua sisi: A dan B. Kalau kandidat memilih A, skor dimensi A bertambah. Kalau memilih B, skor dimensi B bertambah.');
    addParagraph('Rumusnya: skor dimensi = (selected / appearances) x 100. Contoh sederhana: jika dimensi Ekstrover muncul 3 kali lalu dipilih 2 kali, maka skornya 66,7%.', 10);
    addBullets([
      '>= 70% berarti kecenderungan tinggi pada dimensi tersebut.',
      '<= 45% berarti kecenderungan rendah pada dimensi tersebut.',
      'Nilai di tengah berarti hasilnya seimbang / sedang.',
    ]);

    ensureSpace(50);
    addSectionTitle('Tips Cepat');
    addBullets([
      'Gunakan download PDF detail untuk arsip kandidat.',
      'Gunakan export Excel jika ingin rekap data lebih cepat.',
      'Gunakan reset data hanya jika kandidat memang perlu test ulang.',
    ]);

      doc.save(`panduan_recruiter_dashboard_${isAdmin ? 'admin' : 'recruiter'}.pdf`);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message || 'Gagal membuat PDF tutorial' });
    }
  };

  const downloadExamProcedurePdf = () => {
    const doc = new jsPDF();
    const marginLeft = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (marginLeft * 2);
    let cursorY = 16;

    const addSectionTitle = (title) => {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, marginLeft, cursorY);
      cursorY += 8;
    };

    const addParagraph = (text, size = 11) => {
      doc.setFontSize(size);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(text, contentWidth);
      doc.text(lines, marginLeft, cursorY);
      cursorY += lines.length * 5 + 2;
    };

    const addBullets = (items) => {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      items.forEach((item) => {
        const lines = doc.splitTextToSize(item, contentWidth - 6);
        doc.text(`• ${lines[0]}`, marginLeft, cursorY);
        if (lines.length > 1) {
          doc.text(lines.slice(1), marginLeft + 6, cursorY + 5);
          cursorY += lines.length * 5;
        } else {
          cursorY += 5;
        }
      });
      cursorY += 2;
    };

    const ensureSpace = (needed) => {
      if (cursorY + needed > 275) {
        doc.addPage();
        cursorY = 16;
      }
    };

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Tata Cara Pengerjaan Exam', marginLeft, cursorY);
    cursorY += 8;
    addParagraph('Panduan ini menjelaskan langkah mengerjakan ujian, mulai dari masuk ke sesi, menyiapkan foto awal, menjawab soal, sampai selesai submit.');

    ensureSpace(50);
    addSectionTitle('Sebelum Memulai');
    addBullets([
      'Pastikan perangkat kamera dan koneksi internet stabil.',
      'Siapkan posisi duduk yang terang agar foto sesi terekam dengan jelas.',
      'Baca petunjuk ujian dan durasi yang diberikan sebelum memulai.',
    ]);

    ensureSpace(55);
    addSectionTitle('Langkah Pengerjaan');
    addBullets([
      'Login ke akun kandidat lalu buka menu ujian yang tersedia.',
      'Klik tombol mulai ujian dan ambil foto awal jika diminta oleh sistem.',
      'Kerjakan soal satu per satu sesuai instruksi pada layar.',
      'Perhatikan apakah soal berupa pilihan ganda atau essay.',
      'Jika pilihan ganda, pilih jawaban yang paling sesuai sebelum pindah ke soal berikutnya.',
      'Jika essay, ketik jawaban lengkap dan jelas pada kolom jawaban.',
    ]);

    ensureSpace(55);
    addSectionTitle('Aturan Saat Ujian');
    addBullets([
      'Jangan keluar dari halaman ujian tanpa alasan karena sistem dapat mencatat pelanggaran.',
      'Jangan membuka aplikasi atau tab lain jika aturan ujian melarangnya.',
      'Tetap berada di depan kamera agar foto dan aktivitas ujian bisa terpantau.',
      'Jika kamera atau koneksi terganggu, segera hubungi admin atau recruiter.',
    ]);

    ensureSpace(50);
    addSectionTitle('Setelah Selesai');
    addParagraph('Setelah semua soal selesai, klik submit atau selesai ujian. Sistem akan menyimpan jawaban, foto sesi, dan jika ada, catatan pelanggaran untuk bahan penilaian recruiter.');

    doc.save('panduan_pengerjaan_exam.pdf');
  };

  const downloadPsychotestProcedurePdf = () => {
    const doc = new jsPDF();
    const marginLeft = 14;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (marginLeft * 2);
    let cursorY = 16;

    const addSectionTitle = (title) => {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, marginLeft, cursorY);
      cursorY += 8;
    };

    const addParagraph = (text, size = 11) => {
      doc.setFontSize(size);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(text, contentWidth);
      doc.text(lines, marginLeft, cursorY);
      cursorY += lines.length * 5 + 2;
    };

    const addBullets = (items) => {
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      items.forEach((item) => {
        const lines = doc.splitTextToSize(item, contentWidth - 6);
        doc.text(`• ${lines[0]}`, marginLeft, cursorY);
        if (lines.length > 1) {
          doc.text(lines.slice(1), marginLeft + 6, cursorY + 5);
          cursorY += lines.length * 5;
        } else {
          cursorY += 5;
        }
      });
      cursorY += 2;
    };

    const ensureSpace = (needed) => {
      if (cursorY + needed > 275) {
        doc.addPage();
        cursorY = 16;
      }
    };

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Tata Cara Pengerjaan Psikotest', marginLeft, cursorY);
    cursorY += 8;
    addParagraph('Panduan ini menjelaskan cara mengikuti psikotest, dari pengecekan awal kamera sampai cara memilih jawaban A atau B.');

    ensureSpace(50);
    addSectionTitle('Sebelum Memulai');
    addBullets([
      'Pastikan kamera aktif karena sistem dapat mengambil foto awal sesi.',
      'Cari tempat yang tenang dan pencahayaan cukup.',
      'Siapkan fokus karena psikotest menilai kecenderungan jawaban, bukan jawaban benar atau salah.',
    ]);

    ensureSpace(55);
    addSectionTitle('Langkah Pengerjaan');
    addBullets([
      'Login ke akun kandidat lalu buka menu psikotest.',
      'Klik mulai psikotest dan izinkan sistem mengambil foto awal jika diminta.',
      'Baca dua pernyataan pada setiap soal dengan teliti.',
      'Pilih sisi A atau B yang paling sesuai dengan diri Anda.',
      'Kerjakan sampai semua soal selesai tanpa terlalu lama berpikir pada satu soal.',
    ]);

    ensureSpace(55);
    addSectionTitle('Hal Penting Saat Psikotest');
    addBullets([
      'Tidak ada jawaban benar atau salah, jadi pilih yang paling menggambarkan Anda.',
      'Jawab dengan konsisten agar hasil penilaian lebih akurat.',
      'Jangan menutup kamera atau berpindah halaman jika sistem melarangnya.',
      'Jika ada gangguan teknis, segera laporkan ke admin atau recruiter.',
    ]);

    ensureSpace(50);
    addSectionTitle('Setelah Selesai');
    addParagraph('Setelah jawaban terkirim, recruiter dapat melihat hasil, detail pertanyaan, jawaban, foto awal, dan ringkasan kecenderungan psikotest pada halaman detail kandidat.');

    doc.save('panduan_pengerjaan_psikotest.pdf');
  };

  const closeChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Semua field password wajib diisi' });
      return;
    }

    if (newPassword.length < 6) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Password baru minimal 6 karakter' });
      return;
    }

    if (newPassword !== confirmPassword) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Konfirmasi password baru tidak cocok' });
      return;
    }

    try {
      setChangingPassword(true);
      const res = await API.put('/auth/change-password', {
        currentPassword,
        newPassword
      });
      Swal.fire({ icon: 'success', title: 'Berhasil', text: res.data?.message || 'Password berhasil diubah', timer: 1800, showConfirmButton: false });
      closeChangePasswordModal();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message || 'Gagal mengubah password' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = async () => {
    const result = await Swal.fire({
      icon: 'question',
      title: 'Keluar akun?',
      text: 'Anda akan diarahkan ke halaman login.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Logout',
      cancelButtonText: 'Batal',
    });

    if (!result.isConfirmed) return;

    setShowUserMenu(false);
    logout();
  };

  const openRecruiterForm = (rec = null) => {
    if (rec) {
      setEditingRecruiterId(rec.id);
      setRecruiterForm({
        name: rec.name || '',
        email: rec.email || '',
        password: ''
      });
    } else {
      setEditingRecruiterId(null);
      setRecruiterForm({ name: '', email: '', password: '' });
    }
    setShowRecruiterForm(true);
  };

  const saveRecruiter = async () => {
    if (!recruiterForm.name.trim() || !recruiterForm.email.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Nama dan email wajib diisi' });
      return;
    }
    if (!editingRecruiterId && !recruiterForm.password.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Password wajib diisi saat tambah recruiter' });
      return;
    }
    if (recruiterForm.password && recruiterForm.password.length < 6) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Password minimal 6 karakter' });
      return;
    }

    try {
      if (editingRecruiterId) {
        await API.put(`/admin/recruiters/${editingRecruiterId}`, recruiterForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Recruiter berhasil diperbarui', timer: 1500, showConfirmButton: false });
      } else {
        await API.post('/admin/recruiters', recruiterForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Recruiter berhasil ditambahkan', timer: 1500, showConfirmButton: false });
      }

      setShowRecruiterForm(false);
      setEditingRecruiterId(null);
      setRecruiterForm({ name: '', email: '', password: '' });
      const res = await API.get('/admin/recruiters');
      setRecruiters(res.data || []);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const removeRecruiter = async (recruiterId) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Recruiter?',
      text: 'Recruiter akan dihapus dari sistem.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;

    try {
      await API.delete(`/admin/recruiters/${recruiterId}`);
      Swal.fire({ icon: 'success', title: 'Dihapus', text: 'Recruiter berhasil dihapus', timer: 1500, showConfirmButton: false });
      const res = await API.get('/admin/recruiters');
      setRecruiters(res.data || []);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

    const savePosition = async () => {
    if (!positionForm.position_cd.trim() || !positionForm.position_name.trim() || !positionForm.departement.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'All Fields are Required' });
      return;
    }

    try {
      if (editingPositionId) {
        await API.put(`/posisi/${editingPositionId}/update`, positionForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Master Posisi diperbarui', timer: 1500, showConfirmButton: false });
      } else {
        await API.post('/posisi/add', positionForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Master Posisi berhasil ditambahkan', timer: 1500, showConfirmButton: false });
      }

      setShowPositionForm(false);
      setEditingPositionId(null);
      setPositionForm({ position_cd: '', position_name: '', departement: '' });
      const res = await API.get('/posisi');
      setPositions(res.data || []);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const removeMasterPosition = async (id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Master data Posisi?',
      text: 'Data akan dihapus dari sistem.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;

    try {
      await API.delete(`/posisi/${id}/delete`);
      Swal.fire({ icon: 'success', title: 'Dihapus', text: 'Master Data Posisi berhasil dihapus', timer: 1500, showConfirmButton: false });
      const res = await API.get('/posisi');
      setPositions(res.data || []);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const openPositionsForm = (p = null) => {
    if (p) {
      setEditingPositionId(p.id);
      setPositionForm({
        position_cd: p.position_cd || '',
        position_name: p.position_name || '',
        departement: p.departement || '',
      });
    } else {
      setEditingPositionId(null);
      setPositionForm({ name: '', email: '', password: '' });
    }
    setShowPositionForm(true);
  };

  // ─── Helper: Create default schedule with current time ────────────────────────
const createDefaultCandidateSchedule = (examsArray = []) => {
  const now = new Date();
  const formatDateTime = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const modules = [];
  let currentTime = new Date(now);

  const psyStart = formatDateTime(currentTime);
  currentTime.setHours(currentTime.getHours() + 1);
  modules.push({
    module_type: 'psychotest',
    module_label: 'Psikotest',
    exam_id: null,
    sequence_no: 1,
    is_active: 1,
    starts_at: psyStart,
    ends_at: formatDateTime(currentTime),
  });

  examsArray.forEach((exam, idx) => {
    currentTime.setHours(currentTime.getHours() + 1);
    const examStart = formatDateTime(currentTime);
    currentTime.setHours(currentTime.getHours() + (exam.duration_minutes / 60 || 1));
    modules.push({
      module_type: 'exam',
      module_label: exam.title,
      exam_id: exam.id,
      sequence_no: idx + 2,
      is_active: 1,
      starts_at: examStart,
      ends_at: formatDateTime(currentTime)
    });
  });

  return modules;
};


  const formatForDatetimeLocalInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

const buildSchedulePayload = (modules) => ({
  modules: modules.map(m => ({
    module_type: m.module_type,
    exam_id: m.exam_id,
    sequence_no: m.sequence_no,
    starts_at: m.starts_at,
    ends_at: m.ends_at,
    auto_open: m.auto_open === 0 || m.auto_open === false ? 0 : 1,
    is_active: m.is_active === 0 || m.is_active === false ? 0 : 1
  }))
});

const validateScheduleModules = (modules) => {

  if (!Array.isArray(modules) || modules.length === 0) {
    return 'Data jadwal modul wajib diisi';
  }
  console.log(
    candidateScheduleModules.map(m => ({
        modul: m.module_label,
        is_active: m.is_active,
        type: typeof m.is_active
    }))
);
  const activeModules = modules.filter(
    m => m.is_active !== 0
);
  if (activeModules.length === 0) {
    return 'Minimal harus ada 1 modul yang aktif';
  }

  const seenSequences = new Set();

  for (const m of activeModules) {

    const seq = Number(m.sequence_no);

    if (!Number.isFinite(seq) || seq < 1) {
      return 'Urutan modul harus berupa angka lebih dari 0';
    }

    if (seenSequences.has(seq)) {
      return 'Urutan modul tidak boleh duplikat';
    }

    seenSequences.add(seq);

    if (m.module_type === 'exam' && !m.exam_id) {
      return `Ujian "${m.module_label || m.title || ''}" tidak memiliki exam_id yang valid`;
    }

    if (m.starts_at && m.ends_at) {

      const start = new Date(m.starts_at);
      const end = new Date(m.ends_at);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return `Format waktu tidak valid pada modul "${m.module_label || m.title || ''}"`;
      }

      if (end <= start) {
        return `Waktu selesai harus lebih besar dari waktu mulai pada modul "${m.module_label || m.title || ''}"`;
      }
    }
  }

  return null;
};
  const canEditCandidateScheduleModule = (index, module) => {
    const status = candidateModuleStatuses[index] || 'belum_dimulai';
    return !['selesai', 'sedang_berlangsung', 'gagal', 'diskualifikasi'].includes(status);
  };

  const canReorderCandidateScheduleModule = (index) => {
  const status = candidateModuleStatuses[index] || 'belum_dimulai';
  return !['selesai', 'sedang_berlangsung', 'gagal', 'diskualifikasi'].includes(status);
};

  // ─── Helper: Get status badge info ─────────────────────────────────────────────
  const getModuleStatusInfo = (index) => {
    const status = candidateModuleStatuses[index] || 'belum_dimulai';
    const statusMap = {
      'selesai': { label: '✅ Selesai', color: '#4caf50', bg: '#e8f5e9' },
      'sedang_berlangsung': { label: '▶ Sedang Berlangsung', color: '#ff9800', bg: '#fff3e0' },
      'gagal': { label: '❌ Gagal', color: '#f44336', bg: '#ffebee' },
      'diskualifikasi': { label: '🚫 Diskualifikasi', color: '#d32f2f', bg: '#ffcdd2' },
      'belum_dimulai': { label: '⏳ Belum Dimulai', color: '#2196f3', bg: '#e3f2fd' }
    };
    return statusMap[status] || statusMap['belum_dimulai'];
  };

  // ─── Helper: Move module up (decrease sequence_no) ──────────────────────────────
  const moveCandidateScheduleModuleUp = (index) => {
  if (index <= 0) return; 
    const updated = [...candidateScheduleModules];
    const temp = updated[index];
    updated[index] = updated[index - 1];
    updated[index - 1] = temp;
    updated.forEach((mod, i) => { mod.sequence_no = i + 1; });
    setCandidateScheduleModules(updated);
  };

  // ─── Helper: Move module down (increase sequence_no) ───────────────────────────
  const moveCandidateScheduleModuleDown = (index) => {
    if (index >= candidateScheduleModules.length - 1) return;
    const updated = [...candidateScheduleModules];
    const temp = updated[index];
    updated[index] = updated[index + 1];
    updated[index + 1] = temp;
    updated.forEach((mod, i) => { mod.sequence_no = i + 1; });
    setCandidateScheduleModules(updated);
  };

  // ─── Open Candidate Form (New or Edit) ─────────────────────────────────────
  const openCandidateForm = async (forEdit = false) => {
  if (!forEdit) {
    setInvitationSchedules(['']);
    setEditingCandidateId(null);
    setCandidateForm({ name: '', email: '', password: '', position: 'general' });
    setCandidateModuleStatuses({});
    try {
      const examsRes = await API.get('/recruiter/exams');
      const examsArray = examsRes.data || [];
      setCandidateFormExams(examsArray);
      const filteredExams = examsArray.filter(
        (e) => e.position === 'all' || e.position === 'general'
      );
      setCandidateScheduleModules(createDefaultCandidateSchedule(filteredExams));
    } catch (err) {
      console.error('Failed to fetch exams:', err);
      setCandidateFormExams([]);
      setCandidateScheduleModules(createDefaultCandidateSchedule([]));
    }
  }
  setShowCandidateForm(true);
};
  const closeCandidateForm = () => {
    setShowCandidateForm(false);
    setInvitationSchedules(['']);
  };

const editCandidate = (sess) => {
  setEditingCandidateId(sess.candidate_id);
  setCandidateForm({
    name: sess.candidate_name || '',
    email: sess.candidate_email || '',
    password: '',
    position: sess.position || 'general'
  });

  setCandidateModuleStatuses({});

  const fetchScheduleAndExams = async () => {
    try {
      const [detailRes, examsRes] = await Promise.all([
        API.get(`/recruiter/candidate/${sess.candidate_id}`),
        API.get('/recruiter/exams'),
      ]);

      const examsArray = examsRes.data || [];
      setCandidateFormExams(examsArray);

      const existingModules = detailRes.data?.schedule_modules;

      if (Array.isArray(existingModules) && existingModules.length > 0) {
        setCandidateScheduleModules(existingModules);

        const statusMap = {
          completed: 'selesai',
          active: 'sedang_berlangsung',
          failed: 'gagal',
          disqualified: 'diskualifikasi',
          pending: 'belum_dimulai',
        };
        const statuses = {};
        existingModules.forEach((mod, idx) => {
          statuses[idx] = statusMap[mod.module_status] || 'belum_dimulai';
        });
        setCandidateModuleStatuses(statuses);
      } else {
        const filteredExams = examsArray.filter(
          (e) => e.position === 'all' || e.position === (sess.position || 'general')
        );
        setCandidateScheduleModules(createDefaultCandidateSchedule(filteredExams));
        setCandidateModuleStatuses({});
      }
    } catch (err) {
      console.error('Failed to load schedule/exams:', err);
      setCandidateFormExams([]);
      setCandidateScheduleModules(createDefaultCandidateSchedule([]));
      setCandidateModuleStatuses({});
    }
  };
  fetchScheduleAndExams();
  setShowCandidateForm(true);
};


  const deleteCandidateRow = async (candidate_id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Peserta?',
      text: 'Peserta akan dihapus dari sistem.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;

    try {
      await API.delete(`/recruiter/candidate/${candidate_id}`);
      Swal.fire({ icon: 'success', title: 'Dihapus', text: 'Peserta berhasil dihapus', timer: 1500, showConfirmButton: false });
      const res = await API.get('/recruiter/sessions');
      setSessions(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

const saveCandidate = async () => {
  if (!candidateForm.name.trim() || !candidateForm.email.trim() || !candidateForm.position.trim()) {
    Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Nama, email, dan posisi harus diisi' });
    return;
  }

  if (candidateScheduleModules.length > 0) {
    const scheduleError = validateScheduleModules(candidateScheduleModules);
    if (scheduleError) {
      Swal.fire({ icon: 'warning', title: 'Jadwal Tidak Valid', text: scheduleError });
      return;
    }
  }

  console.log('Position:', candidateForm.position);
console.log('Schedule payload:', buildSchedulePayload(candidateScheduleModules));

  try {
    if (editingCandidateId) {
      await API.put(`/recruiter/candidate/${editingCandidateId}`, { ...candidateForm });
      
      if (candidateScheduleModules.length > 0) {
        try {
          await API.put(`/recruiter/candidate/${editingCandidateId}/schedules`, buildSchedulePayload(candidateScheduleModules));
        } catch (scheduleErr) {
          console.error('Failed to save schedule:', scheduleErr);
          Swal.fire({ icon: 'warning', title: 'Peringatan', text: scheduleErr.response?.data?.message || 'Gagal menyimpan jadwal' });
        }
      }
      
      Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Data peserta berhasil diperbarui', timer: 1500, showConfirmButton: false });
    } else {
      const scheduleList = candidateScheduleModules
        .filter((m) => m.is_active !== 0 && m.starts_at && m.ends_at)
        .sort((a, b) => a.sequence_no - b.sequence_no)
        .map((m) => {
          const label = m.module_label || m.title || (m.module_type === 'psychotest' ? 'Psikotest' : 'Ujian');
          const start = new Date(m.starts_at).toLocaleString('id-ID', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Asia/Jakarta',
          });
          const end = new Date(m.ends_at).toLocaleString('id-ID', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Asia/Jakarta',
          });
          return `${label}: ${start} s/d ${end} WIB`;
        });

      const res = await API.post('/recruiter/register-candidate', {
        name: candidateForm.name,
        email: candidateForm.email,
        password: candidateForm.password,
        position: candidateForm.position,
        schedules: scheduleList, 
      }); 
      
      const newCandidateId = res.data?.candidate_id;
      if (newCandidateId && candidateScheduleModules.length > 0) {
        try {
          await API.put(`/recruiter/candidate/${newCandidateId}/schedules`, buildSchedulePayload(candidateScheduleModules));
        } catch (scheduleErr) {
          console.error('Failed to save schedule:', scheduleErr);
          Swal.fire({ icon: 'warning', title: 'Peringatan', text: scheduleErr.response?.data?.message || 'Gagal menyimpan jadwal' });
        }
      }
      
      Swal.fire({ icon: 'success', title: 'Peserta berhasil terdaftar', timer: 1500, showConfirmButton: false });
    }
    setShowCandidateForm(false);
    setEditingCandidateId(null);
    setCandidateForm({ name: '', email: '', password: '', position: 'general' });
    setCandidateScheduleModules([]);
    const res = await API.get('/recruiter/sessions');
    setSessions(res.data);
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
  }
};

const uploadCandidatesExcelFile = async () => {
  if (!candidateExcelFile) {
    Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih file Excel terlebih dahulu' });
    return;
  }
  setUploadingCandidates(true);
  try {
    const formData = new FormData();
    formData.append('file', candidateExcelFile);
    const res = await API.post('/recruiter/candidates/upload-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    const { results } = res.data;
    const failedList = (results?.failed || []).map((f) => `${f.email}: ${f.reason}`).join('\n');

    Swal.fire({
      icon: results?.failed?.length > 0 ? 'warning' : 'success',
      title: 'Upload Selesai',
      html: `${res.data.message}${failedList ? `<br/><br/><small style="white-space:pre-line">${failedList}</small>` : ''}`,
    });

    setCandidateExcelFile(null);
    const sessionsRes = await API.get('/recruiter/sessions');
    setSessions(sessionsRes.data);
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
  } finally {
    setUploadingCandidates(false);
  }
};
  // ─── Exam CRUD ──────────────────────────────────────────────────────────────
  const saveExam = async () => {
    if (!examForm.title.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Judul ujian harus diisi' });
      return;
    }
    try {
      if (editingExam) {
        await API.put(`/recruiter/exam/${editingExam.id}`, examForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Ujian berhasil diperbarui', timer: 1500, showConfirmButton: false });
      } else {
        await API.post('/recruiter/exam', examForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Ujian berhasil dibuat', timer: 1500, showConfirmButton: false });
      }
      setShowExamForm(false);
      setEditingExam(null);
      setExamForm({ title: '', description: '', duration_minutes: 60, max_violations: 1, total: 0, multiple_choice: 1, passing_grade: 60, position: 'all' });
      // Reload exams
      const res = await API.get('/recruiter/exams');
      setExams(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const deleteExam = async (exam_id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Ujian?',
      text: 'Ujian dan semua soalnya akan dihapus permanen.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      await API.delete(`/recruiter/exam/${exam_id}`);
      Swal.fire({ icon: 'success', title: 'Dihapus', text: 'Ujian berhasil dihapus', timer: 1500, showConfirmButton: false });
      const res = await API.get('/recruiter/exams');
      setExams(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

// const addScheduleRow = () => {
//   setInvitationSchedules((prev) => [...prev, '']);
// };

// const removeScheduleRow = (index) => {
//   setInvitationSchedules((prev) => prev.filter((_, i) => i !== index));
// };

// const updateScheduleRow = (index, value) => {
//   setInvitationSchedules((prev) => {
//     const updated = [...prev];
//     updated[index] = value;
//     return updated;
//   });
// };

  // ─── Register Candidate ─────────────────────────────────────────────────────
//   const registerCandidate = async () => {
//     if (!candidateForm.name || !candidateForm.email || !candidateForm.password) {
//       Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Semua field harus diisi' });
//       return;
//     }
//     setRegistering(true);
//     try {
//       const regRes = await API.post('/recruiter/register-candidate', {
//         name: candidateForm.name,
//         email: candidateForm.email,
//         password: candidateForm.password,
//         position: candidateForm.position
//       });
      
//       const newCandidateId = regRes.data?.candidate_id;
      
//       let scheduleFailed = false;
// if (newCandidateId && candidateScheduleModules.length > 0) {
//   try {
//     await API.put(`/recruiter/candidate/${newCandidateId}/schedules`, buildSchedulePayload(candidateScheduleModules));
//   } catch (scheduleErr) {
//     scheduleFailed = true;
//     console.error('Failed to save schedule:', scheduleErr);
//   }
// }

// Swal.fire({
//   icon: scheduleFailed ? 'warning' : 'success',
//   title: scheduleFailed ? 'Peserta terdaftar, tapi jadwal gagal disimpan' : 'Berhasil',
//   text: scheduleFailed ? 'Silakan edit peserta untuk atur ulang jadwal.' : 'Peserta berhasil terdaftar',
//   ...(scheduleFailed ? {} : { timer: 1500, showConfirmButton: false })
// });
//       setCandidateForm({ name: '', email: '', password: '', position: 'general' });
//       setCandidateScheduleModules([]);
//       setShowCandidateForm(false);
//       const res = await API.get('/recruiter/sessions');
//       setSessions(res.data);
//     } catch (err) {
//       Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
//     } finally {
//       setRegistering(false);
//     }
//   };

  const startEditExam = (exam) => {
    setEditingExam(exam);
    setExamForm({
      title: exam.title,
      description: exam.description,
      duration_minutes: exam.duration_minutes,
      max_violations: exam.max_violations,
      total: exam.total || 0,
      multiple_choice: exam.multiple_choice !== undefined ? exam.multiple_choice : 1,
      passing_grade: exam.passing_grade || 60,
      position: exam.position || 'all'
    });
    setShowExamForm(true);
  };

// ─── Untuk Exam ──────────────────────────────────────────────────────────────
const toggleSelectAllExamViolations = () => {
  if (selectedViolations.size === sessionDetail?.violations?.length) {
    setSelectedViolations(new Set());
  } else {
    setSelectedViolations(new Set((sessionDetail?.violations || []).map((v) => v.id)));
  }
};

const deleteSelectedExamViolations = async () => {
  if (selectedViolations.size === 0) return;

  const session = sessionDetail?.session;

const sessionId = session?.id || null;
const candidateId = session?.user_id || null;

console.log('[DELETE EXAM VIOLATIONS]', {
  violation_ids: Array.from(selectedViolations),
  candidate_id: candidateId,
  session_id: sessionId,
});

  const confirm = await Swal.fire({
    icon: 'warning',
    title: `Hapus ${selectedViolations.size} pelanggaran?`,
    text: 'Jika total pelanggaran tersisa kurang dari batas maksimal, kandidat akan direaktivasi.',
    showCancelButton: true,
    confirmButtonText: 'Ya, Hapus',
    cancelButtonText: 'Batal',
    confirmButtonColor: '#ef4444',
  });
  if (!confirm.isConfirmed) return;

  try {
    const res = await API.delete('/recruiter/violations', {
      data: {
  violation_ids: Array.from(selectedViolations),
  candidate_id: candidateId,
  session_id: sessionId,
  attempt_id: null,
},
    });

Swal.fire({ icon: 'success', title: 'Berhasil', text: res.data?.message, timer: 2000, showConfirmButton: false });
setSelectedViolations(new Set());


if (sessionId) {
  try {
    const detailRes = await API.get(`/recruiter/session/${sessionId}`);
    setSessionDetail(detailRes.data);
  } catch (e) {
    console.error('[REFRESH SESSION DETAIL ERROR]', e.message);
    setSessionDetail(null);
  }
}

const sessionsRes = await API.get('/recruiter/sessions');
setSessions(sessionsRes.data);
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
  }
};
const toggleViolationSelect = (id) => {
  setSelectedViolations((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
};
// ─── Untuk Psikotest ─────────────────────────────────────────────────────────
const toggleSelectAllPsychotestViolations = () => {
  if (selectedViolations.size === psychotestDetail?.violations?.length) {
    setSelectedViolations(new Set());
  } else {
    setSelectedViolations(new Set((psychotestDetail?.violations || []).map((v) => v.id)));
  }
};

const deleteSelectedPsychotestViolations = async () => {
  if (selectedViolations.size === 0) return;

  const confirm = await Swal.fire({
    icon: 'warning',
    title: `Hapus ${selectedViolations.size} pelanggaran?`,
    text: 'Jika total pelanggaran tersisa kurang dari batas maksimal, kandidat akan direaktivasi.',
    showCancelButton: true,
    confirmButtonText: 'Ya, Hapus',
    cancelButtonText: 'Batal',
    confirmButtonColor: '#ef4444',
  });
  if (!confirm.isConfirmed) return;

  console.log('[PSYCHOTEST DETAIL]', psychotestDetail); 

  try {
    const res = await API.delete('/recruiter/violations', {
      data: {
        violation_ids: Array.from(selectedViolations),
        candidate_id: psychotestDetail?.candidate_id 
          || psychotestDetail?.user_id               
          || null,
        session_id: null,
        attempt_id: psychotestDetail?.attempt_id
          || psychotestDetail?.id
          || null,
      },
    });

    Swal.fire({ icon: 'success', title: 'Berhasil', text: res.data?.message, timer: 2000, showConfirmButton: false });
    setSelectedViolations(new Set());
    const attemptId = psychotestDetail?.id;

    const detailRes = await API.get(
      `/recruiter/psychotest/attempt/${attemptId}`
    );
    setPsychotestDetail(detailRes.data.attempt);
    const sessionsRes = await API.get('/recruiter/sessions');
    setSessions(sessionsRes.data);
  } catch (err) {
    Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
  }
};

  // ─── Psychotest CRUD ───────────────────────────────────────────────────────
  const resetPsychotestForm = () => {
    setEditingPsychotestQuestion(null);
    setPsychotestForm({
      dimension_a: 'extro_intro',
      statement_a: '',
      dimension_b: 'emotional_stability',
      statement_b: '',
      is_active: 1,
    });
  };

  const reloadPsychotestQuestions = async () => {
    const res = await API.get('/recruiter/psychotest/questions');
    setPsychotestQuestions(res.data);
    setPsychotestPage(1);
    setSelectedPsychotestQuestionIds([]);
    setSelectAllPsychotestQuestions(false);
  };

  const savePsychotestQuestion = async () => {
    if (!psychotestForm.statement_a.trim() || !psychotestForm.statement_b.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pernyataan A dan B harus diisi' });
      return;
    }
    if (psychotestForm.dimension_a === psychotestForm.dimension_b) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Dimensi A dan B harus berbeda' });
      return;
    }

    try {
      if (editingPsychotestQuestion) {
        await API.put(`/recruiter/psychotest/question/${editingPsychotestQuestion.id}`, psychotestForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Soal psikotest diperbarui', timer: 1500, showConfirmButton: false });
      } else {
        await API.post('/recruiter/psychotest/question', psychotestForm);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Soal psikotest ditambahkan', timer: 1500, showConfirmButton: false });
      }
      setShowPsychotestForm(false);
      resetPsychotestForm();
      await reloadPsychotestQuestions();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const startEditPsychotestQuestion = (row) => {
    setEditingPsychotestQuestion(row);
    setPsychotestForm({
      dimension_a: row.dimension_a,
      statement_a: row.statement_a,
      dimension_b: row.dimension_b,
      statement_b: row.statement_b,
      is_active: Number(row.is_active) === 1 ? 1 : 0,
    });
    setShowPsychotestForm(true);
  };

  const deletePsychotestQuestion = async (question_id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Soal Psikotest?',
      text: 'Jika soal sudah dipakai, sistem akan menonaktifkan soal (soft delete).',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      await API.delete(`/recruiter/psychotest/question/${question_id}`);
      Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Soal diproses', timer: 1500, showConfirmButton: false });
      await reloadPsychotestQuestions();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const uploadPsychotestExcelFile = async () => {
    if (!psychotestExcelFile) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih file Excel terlebih dahulu' });
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', psychotestExcelFile);
      const res = await API.post('/recruiter/psychotest/questions/upload-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      Swal.fire({ icon: 'success', title: 'Berhasil', text: res.data.message, timer: 2000, showConfirmButton: false });
      setPsychotestExcelFile(null);
      await reloadPsychotestQuestions();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  // const bulkDeleteAllPsychotestQuestions = async () => {
  //   const result = await Swal.fire({
  //     icon: 'warning',
  //     title: 'Hapus Semua Soal Psikotest?',
  //     text: 'Semua bank soal psikotest akan diproses. Soal yang sudah pernah dipakai akan dinonaktifkan, sisanya dihapus permanen.',
  //     showCancelButton: true,
  //     confirmButtonText: 'Ya, Hapus Semua',
  //     cancelButtonText: 'Batal',
  //     confirmButtonColor: '#ef4444',
  //   });
  //   if (!result.isConfirmed) return;

  //   try {
  //     const res = await API.post('/recruiter/psychotest/questions/bulk-delete');
  //     Swal.fire({ icon: 'success', title: 'Berhasil', text: res.data?.message || 'Soal psikotest diproses', timer: 2200, showConfirmButton: false });
  //     await reloadPsychotestQuestions();
  //   } catch (err) {
  //     Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
  //   }
  // ;}

  const togglePsychotestQuestionSelection = (question_id, checked) => {
    setSelectedPsychotestQuestionIds((prev) => {
      const next = checked
        ? [...new Set([...prev, question_id])]
        : prev.filter((id) => id !== question_id);

      setSelectAllPsychotestQuestions(
        psychotestQuestions.length > 0 && next.length === psychotestQuestions.length
      );
      return next;
    });
  };

  const toggleSelectAllPsychotestQuestions = () => {
    setSelectAllPsychotestQuestions((prev) => {
      const next = !prev;
      if (next) {
        setSelectedPsychotestQuestionIds(psychotestQuestions.map((q) => q.id));
      } else {
        setSelectedPsychotestQuestionIds([]);
      }
      return next;
    });
  };

  const bulkDeleteSelectedPsychotestQuestions = async () => {
    if (selectedPsychotestQuestionIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih minimal satu soal psikotest' });
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: `Hapus ${selectedPsychotestQuestionIds.length} Soal Psikotest?`,
      text: 'Soal yang sudah pernah dipakai akan dinonaktifkan, sisanya dihapus permanen.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus Terpilih',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;

    try {
      const deleteResults = await Promise.allSettled(
        selectedPsychotestQuestionIds.map((id) => API.delete(`/recruiter/psychotest/question/${id}`))
      );

      const successCount = deleteResults.filter((r) => r.status === 'fulfilled').length;
      const failedCount = deleteResults.length - successCount;

      if (failedCount === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Berhasil',
          text: `${successCount} soal psikotest berhasil diproses`,
          timer: 2200,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'Selesai Sebagian',
          text: `${successCount} berhasil, ${failedCount} gagal. Silakan coba lagi untuk yang gagal.`,
        });
      }

      await reloadPsychotestQuestions();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const savePsychotestDuration = async () => {
    if (!Number.isFinite(Number(psychotestDurationMinutes)) || Number(psychotestDurationMinutes) < 1 || Number(psychotestDurationMinutes) > 180) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Durasi psikotest harus antara 1 sampai 180 menit' });
      return;
    }

    if (!Number.isFinite(Number(psychotestTotalQuestions)) || Number(psychotestTotalQuestions) < 0 || Number(psychotestTotalQuestions) > 500) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Jumlah soal tampil harus antara 0 sampai 500 (0 = semua soal)' });
      return;
    }

    if (!Number.isFinite(Number(psychotestMaxViolations)) || Number(psychotestMaxViolations) < 1 || Number(psychotestMaxViolations) > 20) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Batas maksimal pelanggaran harus antara 1 sampai 20' });
      return;
    }

    setSavingPsychotestDuration(true);
    try {
      await API.put('/recruiter/psychotest/settings', {
        duration_minutes: Number(psychotestDurationMinutes),
        total_questions: Number(psychotestTotalQuestions),
        max_violations: Number(psychotestMaxViolations),
      });
      Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Pengaturan psikotest diperbarui', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    } finally {
      setSavingPsychotestDuration(false);
    }
  };

  // ─── Toggle exam expansion and load questions ───────────────────────────────
  // const toggleExamExpanded = async (exam_id) => {
  //   if (expandedExams[exam_id]) {
  //     setExpandedExams({...expandedExams, [exam_id]: false});
  //     return;
  //   }
  //   setExpandedExams({...expandedExams, [exam_id]: true});
  //   if (selectedExamForQuestions !== exam_id) {
  //     setSelectedExamForQuestions(exam_id);
  //   }
  // };

const toggleExamSelection = (sessionId) => {
  setSelectedExams((prev) =>
    prev.includes(sessionId)
      ? prev.filter((id) => id !== sessionId)
      : [...prev, sessionId]
  );
};

const toggleSelectAllExams = () => {
  const allSessionIds = candidateDetail.exams.map((e) => e.session_id).filter(Boolean);
  setSelectedExams((prev) =>
    prev.length === allSessionIds.length ? [] : allSessionIds
  );
};

  // ─── Question CRUD ──────────────────────────────────────────────────────────
  const uploadQuestionImageFile = async (question_id) => {
    if (!questionPhotoFile) return;
    try {
      const formData = new FormData();
      formData.append('question_id', question_id);
      formData.append('photo', questionPhotoFile);
      await API.post('/recruiter/question/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    } catch (err) {
      console.error('Upload question image error', err);
    }
  };

  const validateQuestionOptions = () => {
    if (!questionForm.question_text.trim()) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pertanyaan harus diisi' });
      return false;
    }
    // Find current exam to check type
    const currentExam = exams.find(e => e.id === selectedExamForQuestions);
    const isMultipleChoice = !currentExam || currentExam.multiple_choice === 1;
    if (isMultipleChoice) {
      const options = ['option_a', 'option_b', 'option_c', 'option_d', 'option_e'];
      const filled = options.filter(opt => questionForm[opt]?.trim());
      if (filled.length < 2) {
        Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Minimal harus ada 2 pilihan jawaban yang diisi' });
        return false;
      }
      const correctKey = `option_${questionForm.correct_answer}`;
      if (!questionForm.correct_answer || !questionForm[correctKey]?.trim()) {
        Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Jawaban benar harus dipilih dari pilihan yang tersedia' });
        return false;
      }
    }
    return true;
  };

  const saveQuestion = async () => {
    if (!validateQuestionOptions()) return;

    try {
      if (editingQuestion) {
        await API.put(`/recruiter/question/${editingQuestion.id}`, questionForm);
        if (questionPhotoFile) await uploadQuestionImageFile(editingQuestion.id);
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Soal berhasil diperbarui', timer: 1500, showConfirmButton: false });
      } else {
        const res = await API.post('/recruiter/question', {
          exam_id: selectedExamForQuestions,
          ...questionForm
        });
        if (res.data.question_id && questionPhotoFile) {
          await uploadQuestionImageFile(res.data.question_id);
        }
        Swal.fire({ icon: 'success', title: 'Berhasil', text: 'Soal berhasil ditambahkan', timer: 1500, showConfirmButton: false });
      }
      setShowQuestionForm(false);
      setEditingQuestion(null);
      setQuestionPhotoFile(null);
      setQuestionPhotoPreview(null);
      setQuestionForm({
        question_text: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        option_e: '',
        correct_answer: 'a',
        points: 10,
        photo: null
      });
      // Reload questions
      const reload = await API.get(`/recruiter/exam/${selectedExamForQuestions}/questions`);
      setQuestions(reload.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const deleteQuestion = async (question_id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Hapus Soal?',
      text: 'Soal ini akan dihapus permanen.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      await API.delete(`/recruiter/question/${question_id}`);
      Swal.fire({ icon: 'success', title: 'Dihapus', text: 'Soal berhasil dihapus', timer: 1500, showConfirmButton: false });
      const res = await API.get(`/recruiter/exam/${selectedExamForQuestions}/questions`);
      setQuestions(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const toggleQuestionSelection = (question_id, checked) => {
    setSelectedQuestionIds(prev => {
      if (checked) return [...new Set([...prev, question_id])];
      return prev.filter(id => id !== question_id);
    });
  };

  const toggleSelectAllQuestions = () => {
    setSelectAllQuestions(prev => {
      const next = !prev;
      if (next) {
        setSelectedQuestionIds(questions.map(q => q.id));
      } else {
        setSelectedQuestionIds([]);
      }
      return next;
    });
  };

  const bulkDeleteSelectedQuestions = async () => {
    if (selectedQuestionIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih minimal satu soal untuk dihapus' });
      return;
    }
    const result = await Swal.fire({
      icon: 'warning',
      title: `Hapus ${selectedQuestionIds.length} Soal?`,
      text: 'Soal yang dipilih akan dihapus permanen.',
      showCancelButton: true,
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal',
      confirmButtonColor: '#ef4444',
    });
    if (!result.isConfirmed) return;
    try {
      await API.post('/recruiter/questions/bulk-delete', { question_ids: selectedQuestionIds });
      Swal.fire({ icon: 'success', title: 'Dihapus', text: 'Soal terpilih berhasil dihapus', timer: 1500, showConfirmButton: false });
      const res = await API.get(`/recruiter/exam/${selectedExamForQuestions}/questions`);
      setQuestions(res.data);
      setSelectedQuestionIds([]);
      setSelectAllQuestions(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const uploadQuestionsExcelFile = async () => {
    if (!excelFile) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih file Excel terlebih dahulu' });
      return;
    }
    if (!selectedExamForQuestions) {
      Swal.fire({ icon: 'warning', title: 'Perhatian', text: 'Pilih ujian terlebih dahulu' });
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('exam_id', selectedExamForQuestions);
      const res = await API.post('/recruiter/questions/upload-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      Swal.fire({ icon: 'success', title: 'Berhasil', text: res.data.message, timer: 2000, showConfirmButton: false });
      setExcelFile(null);
      const qres = await API.get(`/recruiter/exam/${selectedExamForQuestions}/questions`);
      setQuestions(qres.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message });
    }
  };

  const startEditQuestion = (q) => {
    setEditingQuestion(q);
    setQuestionPhotoFile(null);
    setQuestionPhotoPreview(q.photo ? buildUploadUrl('questions', q.photo) : null);
    setQuestionForm({
      question_text: q.question_text,
      option_a: q.option_a,
      option_b: q.option_b,
      option_c: q.option_c,
      option_d: q.option_d,
      option_e: q.option_e || '',
      correct_answer: q.correct_answer,
      points: q.points,
      photo: q.photo || null
    });
    setShowQuestionForm(true);
  };

  // ─── Filters for sessions ───────────────────────────────────────────────────
const filtered = sessions.filter(s => {
  const search = candidateSearch.trim().toLowerCase();
  if (search) {
    const text = `${s.candidate_name || ''} ${s.candidate_email || ''} ${s.position || ''}`.toLowerCase();
    if (!text.includes(search)) return false;
  }
  if (filter === 'all') return true;
  if (filter === 'passed') return (s.overall_status || 'in_progress') === 'lulus';
  if (filter === 'failed') return (s.overall_status || 'in_progress') === 'tidak_lulus';
  if (filter === 'active') return (s.overall_status || 'in_progress') === 'in_progress';
  if (filter === 'selesai_tes') return (s.overall_status || 'in_progress') === 'selesai_tes';   
  if (filter === 'expired') return (s.overall_status || 'in_progress') === 'expired';
  if (filter === 'disqualified') return s.is_disqualified;
  return true;
});

  const effectiveCandidatePerPage = resolvePageSize(candidatePerPage, filtered.length);
  const totalCandidatePages = Math.max(1, Math.ceil(filtered.length / effectiveCandidatePerPage));
  const paginatedCandidates = filtered.slice(
    (candidatePage - 1) * effectiveCandidatePerPage,
    candidatePage * effectiveCandidatePerPage
  );

  const candidateDetailExams = candidateDetail?.exams || [];
  const candidatePsychotestSummary = candidateDetail ? formatPsychotestResult(candidateDetail.candidate?.psychotest_result) : null;
  const effectiveDetailExamPerPage = resolvePageSize(detailExamPerPage, candidateDetailExams.length);
  const totalDetailExamPages = Math.max(1, Math.ceil(candidateDetailExams.length / effectiveDetailExamPerPage));
  const paginatedCandidateDetailExams = candidateDetailExams.slice(
    (detailExamPage - 1) * effectiveDetailExamPerPage,
    detailExamPage * effectiveDetailExamPerPage
  );

  const stats = {
    total: sessions.length,
    active: sessions.filter(s => (s.overall_status || 'in_progress') === 'in_progress').length,
    finished: sessions.filter(s => (s.overall_status || 'in_progress') !== 'in_progress').length,
    disq: sessions.filter(s => s.is_disqualified).length,
  };

  const effectivePsychotestPerPage = resolvePageSize(psychotestPerPage, psychotestQuestions.length);
  const totalPsychotestPages = Math.max(1, Math.ceil(psychotestQuestions.length / effectivePsychotestPerPage));
  const paginatedPsychotestQuestions = psychotestQuestions.slice(
    (psychotestPage - 1) * effectivePsychotestPerPage,
    psychotestPage * effectivePsychotestPerPage
  );

  const psychotestGroups = [...(candidatePsychotestSummary?.groups || [])]
  .sort((a, b) => {
    const order = {
      'HIGH ANALISYS': 0,
      'MIDDLE RANGE': 1,
      'LOW ANALISYS': 2,
    };

    return (order[a.label] ?? 3) - (order[b.label] ?? 3);
  });

  return (
    <div className="rec-page">
      <header className="rec-header">
        <div className="rec-logo">📋 Rekrutmen Dashboard</div>
        <div className="rec-user" ref={userMenuRef}>
          <button
            type="button"
            className="user-menu-trigger"
            onClick={() => setShowUserMenu((prev) => !prev)}
          >
            👤 {user?.name}
            <span className="user-menu-chevron">▾</span>
          </button>
          {showUserMenu && (
            <div className="user-menu-dropdown">
              <button
                type="button"
                className="user-menu-item"
                onClick={downloadDashboardTutorialPdf}
              >
                📘 Tutorial Sistem
              </button>
              <button
                type="button"
                className="user-menu-item"
                onClick={downloadExamProcedurePdf}
              >
                📝 Tata Cara Exam
              </button>
              <button
                type="button"
                className="user-menu-item"
                onClick={downloadPsychotestProcedurePdf}
              >
                🧠 Tata Cara Psikotest
              </button>
              <button
                type="button"
                className="user-menu-item"
                onClick={() => setShowChangePasswordModal(true)}
              >
                🔐 Change Password
              </button>
              <button
                type="button"
                className="user-menu-item danger"
                onClick={handleLogout}
              >
                🚪 Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {showChangePasswordModal && (
        <div className="form-modal" onClick={closeChangePasswordModal}>
          <div className="form-box change-password-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Password</h3>
              <button className="modal-close-btn" onClick={closeChangePasswordModal}>✕</button>
            </div>
            <div className="form-group">
              <label>Password Saat Ini</label>
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                placeholder="Masukkan password saat ini"
              />
            </div>
            <div className="form-group">
              <label>Password Baru</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="Minimal 6 karakter"
              />
            </div>
            <div className="form-group">
              <label>Konfirmasi Password Baru</label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Ulangi password baru"
              />
            </div>
            <div className="form-actions">
              <button className="btn-save" onClick={handleChangePassword} disabled={changingPassword}>
                {changingPassword ? 'Menyimpan...' : 'Simpan Password'}
              </button>
              <button className="btn-cancel" onClick={closeChangePasswordModal}>Batal</button>
            </div>
          </div>
        </div>
      )}

      <div className="rec-body">
        {/* Tab Navigation */}
        <div className="tab-nav">
          <button
            className={`tab-btn ${activeTab === 'candidates' ? 'active' : ''}`}
            onClick={() => setActiveTab('candidates')}
          >
            👥 Peserta Ujian
          </button>
          <button
            className={`tab-btn ${activeTab === 'exams' ? 'active' : ''}`}
            onClick={() => setActiveTab('exams')}
          >
            📝 Kelola Ujian & Soal
          </button>
          <button
            className={`tab-btn ${activeTab === 'psychotest' ? 'active' : ''}`}
            onClick={() => setActiveTab('psychotest')}
          >
            🧠 Kelola Psikotest
          </button>
          {user?.role === 'admin' && (
            <button
              className={`tab-btn ${activeTab === 'recruiters' ? 'active' : ''}`}
              onClick={() => setActiveTab('recruiters')}
            >
              🧑‍💼 Kelola Recruiter
            </button>
          )}
          <button
            className={`tab-btn ${activeTab === 'positions' ? 'active' : ''}`}
            onClick={() => setActiveTab('positions')}
          >
             🧑‍💼 Kelola Posisi
          </button>
        </div>

        {/* ─── CANDIDATES TAB ─────────────────────────────────────────────────*/}
        {activeTab === 'candidates' && (
          <>
            {/* Stats */}
            <div className="rec-stats">
              <div className="stat-card">
                <div className="sc-val">{stats.total}</div>
                <div className="sc-label">Total Peserta</div>
              </div>
              <div className="stat-card active">
                <div className="sc-val">{stats.active}</div>
                <div className="sc-label">Sedang Ujian</div>
              </div>
              <div className="stat-card finished">
                <div className="sc-val">{stats.finished}</div>
                <div className="sc-label">Selesai</div>
              </div>
              <div className="stat-card disq">
                <div className="sc-val">{stats.disq}</div>
                <div className="sc-label">Diskualifikasi</div>
              </div>
            </div>

            <div className="rec-content">
              {/* Left: sessions table */}
              <div className="sessions-panel">
                <div className="panel-header">
                  <h2>Data Peserta</h2>
                  <button className="btn-primary" onClick={() => showCandidateForm ? closeCandidateForm() : openCandidateForm(false)}>
                    {showCandidateForm ? '✕ Tutup Form' : '➕ Daftar Peserta Baru'}
                  </button>
                </div>

                <div className="table-toolbar">
                  <input
                    className="search-input"
                    type="text"
                    placeholder="Cari peserta..."
                    value={candidateSearch}
                    onChange={(e) => setCandidateSearch(e.target.value)}
                  />
                </div>

                {showCandidateForm && (
                  <div className="form-modal">
                    <div className="form-box">
                      <div className="modal-header">
                        <h3>{editingCandidateId ? 'Edit Peserta' : 'Daftar Peserta Baru'}</h3>
                        <button className="modal-close-btn" onClick={closeCandidateForm}>✕</button>
                      </div>
                      <div className="form-group">
                        <label>Nama Lengkap</label>
                        <input
                          type="text"
                          value={candidateForm.name}
                          onChange={(e) => setCandidateForm({...candidateForm, name: e.target.value})}
                          placeholder="Nama peserta"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={candidateForm.email}
                          onChange={(e) => setCandidateForm({...candidateForm, email: e.target.value})}
                          placeholder="email@example.com"
                        />
                      </div>
                      <div className="form-group">
                        <label>Password</label>
                        <input
                          type="password"
                          value={candidateForm.password}
                          onChange={(e) => setCandidateForm({...candidateForm, password: e.target.value})}
                          placeholder="Min. 6 karakter"
                        />
                      </div>
                      <div className="form-group">
                        <label>Posisi</label>
                        <select
                          value={candidateForm.position}
                          onChange={(e) =>
                            setCandidateForm({ ...candidateForm, position: e.target.value })
                          }
                        >
                          <option value="">Pilih Posisi</option>
                          {positions.map((p) => (
                            <option key={p.id} value={p.position_cd || p.position_name}>
                              {p.position_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Schedule Section */}
                      <div className="form-group" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                        <div style={{ marginBottom: '8px', padding: '10px', backgroundColor: '#e3f2fd', borderLeft: '4px solid #2196f3', borderRadius: '3px', fontSize: '12px' }}>
                          <strong>ℹ️ Catatan:</strong>Anda bisa mengatur waktu mulai dan selesai untuk setiap modul. Modul yang sedang berlangsung atau sudah selesai tidak bisa diedit urutan dan waktunya.
                        </div>
                        <label style={{ fontSize: '14px', fontWeight: 'bold' }}>📅 Jadwal Tes</label>
                        {candidateScheduleModules.length > 0 && (
                          <div style={{ marginTop: '12px', overflowX: 'auto' }}>
                            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                                  <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 'bold', width: '50px' }}>NO</th>
                                  <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 'bold', width: '70px' }}>
                                  AKTIF
                                </th>
                                  <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 'bold' }}>MODUL</th>
                                  <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 'bold' }}>STATUS</th>
                                  <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 'bold' }}>WAKTU MULAI</th>
                                  <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 'bold' }}>WAKTU SELESAI</th>
                                  <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 'bold' }}>AKSI</th>
                                </tr>
                              </thead>
                              <tbody>
                                {candidateScheduleModules.map((mod, idx) => {
                                  const canEdit = canEditCandidateScheduleModule(idx, mod);
                                  const canReorder = canReorderCandidateScheduleModule(idx);
                                  const statusInfo = getModuleStatusInfo(idx);
                                  const isEditing = editingCandidateScheduleModuleIndex === idx;
                                  const isModuleActive = mod.is_active === undefined ? false : mod.is_active !== 0;
                                  return (
                                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                      {/* NO */}
                                      <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>{mod.sequence_no || idx + 1}</td>
                                      <td style={{ padding: '8px', textAlign: 'center' }}>
                                        <input
                                          type="checkbox"
                                          checked={isModuleActive}
                                          onChange={(e) => {
                                            const updated = [...candidateScheduleModules];
                                            updated[idx] = {
                                              ...updated[idx],
                                              is_active: e.target.checked ? 1 : 0
                                            };
                                            setCandidateScheduleModules(updated);
                                          }}
                                        />
                                      </td>
                                      {/* MODUL */}
                                      <td style={{ padding: '8px', textAlign: 'left' }}>
                                        <strong>{mod.module_label || mod.title || mod.module_type}</strong>
                                      </td>
                                      
                                      {/* STATUS */}
                                      <td style={{ padding: '8px', textAlign: 'center' }}>
                                        <span style={{ 
                                          display: 'inline-block',
                                          padding: '4px 8px', 
                                          backgroundColor: statusInfo.bg, 
                                          color: statusInfo.color, 
                                          borderRadius: '12px', 
                                          fontSize: '11px',
                                          fontWeight: 'bold'
                                        }}>
                                          {statusInfo.label}
                                        </span>
                                      </td>
                                      
                                    {/* WAKTU MULAI */}
                                      <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px' }}>
                                        {isEditing && canEdit ? (
                                          <input
                                            type="datetime-local"
                                            value={editingCandidateScheduleTime.starts_at}
                                            onChange={(e) => setEditingCandidateScheduleTime({ ...editingCandidateScheduleTime, starts_at: e.target.value })}
                                            style={{ padding: '4px', fontSize: '11px', width: '100%' }}
                                          />
                                        ) : (
                                          mod.starts_at ? new Date(mod.starts_at).toLocaleString('id-ID') : '-'
                                        )}
                                      </td>

                                      {/* WAKTU SELESAI */}
                                      <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px' }}>
                                        {isEditing && canEdit ? (
                                          <input
                                            type="datetime-local"
                                            value={editingCandidateScheduleTime.ends_at}
                                            onChange={(e) => setEditingCandidateScheduleTime({ ...editingCandidateScheduleTime, ends_at: e.target.value })}
                                            style={{ padding: '4px', fontSize: '11px', width: '100%' }}
                                          />
                                        ) : (
                                          mod.ends_at ? new Date(mod.ends_at).toLocaleString('id-ID') : '-'
                                        )}
                                      </td>

                                      {/* AKSI */}
                                      <td style={{ padding: '8px', textAlign: 'center' }}>
                                        {isEditing && canEdit ? (
                                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const updated = [...candidateScheduleModules];
                                                updated[idx].starts_at = editingCandidateScheduleTime.starts_at;
                                                updated[idx].ends_at = editingCandidateScheduleTime.ends_at;
                                                setCandidateScheduleModules(updated);
                                                setEditingCandidateScheduleModuleIndex(null);
                                              }}
                                              style={{ padding: '4px 8px', fontSize: '11px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                                              title="Simpan"
                                            >
                                              ✓
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => setEditingCandidateScheduleModuleIndex(null)}
                                              style={{ padding: '4px 8px', fontSize: '11px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                                              title="Batal"
                                            >
                                              ✕
                                            </button>
                                          </div>
                                        ) : (
                                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                                            {/* Edit button */}
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setEditingCandidateScheduleModuleIndex(idx);
                                                setEditingCandidateScheduleTime({
                                                  starts_at: formatForDatetimeLocalInput(mod.starts_at),
                                                  ends_at: formatForDatetimeLocalInput(mod.ends_at)
                                                });
                                              }}
                                              disabled={!canEdit}
                                              style={{
                                                padding: '4px 8px',
                                                fontSize: '11px',
                                                backgroundColor: canEdit ? '#ff7043' : '#ccc',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '3px',
                                                cursor: canEdit ? 'pointer' : 'not-allowed',
                                                opacity: canEdit ? 1 : 0.5
                                              }}
                                              title={canEdit ? 'Edit Waktu' : 'Tidak bisa diedit'}
                                            >
                                              ✎
                                            </button>

                                            {/* Up arrow - disabled for psychotest or if locked */}
                                             
                                              <button
                                                type="button"
                                                onClick={() => moveCandidateScheduleModuleUp(idx)}
                                                style={{
                                                  padding: '4px 8px',
                                                  fontSize: '11px',
                                                  backgroundColor: '#4db8ff',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '3px',
                                                  cursor:'pointer',
                                                  opacity:  1 
                                                }}
                        
                                              >
                                                ↑
                                              </button>

                                            {/* Down arrow */}
                      
                                              <button
                                                type="button"
                                                onClick={() => moveCandidateScheduleModuleDown(idx)}
                                                style={{
                                                  padding: '4px 8px',
                                                  fontSize: '11px',
                                                  backgroundColor: '#4db8ff',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '3px',
                                                  cursor: 'pointer',
                                                  opacity:  1 
                                                }}
                                                // title={canReorder ? 'Turun Urutan' : 'Tidak bisa dipindahkan'}
                                              >
                                                ↓
                                              </button>
                                            
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      <div className="form-actions">
                        <button className="btn-primary" onClick={saveCandidate} disabled={registering}>
                          {registering ? 'Memproses...' : editingCandidateId ? 'Simpan Perubahan' : 'Daftar'}
                        </button>
                        <button className="btn-secondary" onClick={closeCandidateForm}>
                          Batal
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                            <div className="bulk-actions" style={{ marginBottom: '12px' }}>
                              <label className="upload-label">
                                📤 Upload Excel Peserta:
                                <input
                                  type="file"
                                  accept=".xlsx,.xls"
                                  onChange={(e) => setCandidateExcelFile(e.target.files?.[0] || null)}
                                />
                              </label>
                              <button className="btn-primary" onClick={uploadCandidatesExcelFile} disabled={uploadingCandidates}>
                                {uploadingCandidates ? 'Mengupload...' : 'Upload Excel'}
                              </button>


                              <a className="btn-info link-btn" href="/templates/Template_Register_Peserta.xlsx" download>
                                  ⬇️ Template Registrasi Peserta
                                </a>
                            </div>

                <div className="filter-tabs">
                  {['all','active','selesai_tes','passed','failed', 'expired','disqualified'].map(f => (
                      <button key={f} className={`ftab ${filter===f?'active':''}`} onClick={() => setFilter(f)}>
                        {f === 'all' ? 'Semua Candidate'
                          : f === 'active' ? 'Aktif'
                          : f === 'selesai_tes' ? 'Selesai Tes'
                          : f === 'passed' ? 'Lulus'
                          : f === 'failed' ? 'Tidak Lulus'
                          : f === 'expired' ? 'Expired'
                          : 'Diskualifikasi'}
                      </button>
                    ))}
                </div>

                <div className="bulk-actions" style={{ marginBottom: '12px' }}>
                  <div className="bulk-actions-left">
                    <label className="upload-label">
                      Show List:
                      <select value={candidatePerPage} onChange={(e) => setCandidatePerPage(e.target.value)}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="all">All</option>
                      </select>
                    </label>
                    <button type="button" className="btn-info" onClick={exportCandidatesExcel}>
                      ⬇️ Export Excel
                    </button>
                  </div>
                </div>

                {loadingSessions ? <div className="loading-text">Memuat data...</div> : (
                  <div className="sessions-table">
                    <table>
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>Foto</th>
                          <th>Nama</th>
                          <th>Email</th>
                          <th>Posisi</th>
                          <th>Status</th>
                          <th>Nilai</th>
                          <th>Pelanggaran</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedCandidates.map((s, idx) => {
                          const overallStatus = s.overall_status || 'in_progress';
                          const scoreValue = Number(s.score || 0);
                          const badgeClass =
                            overallStatus === 'lulus' ? 'pass'
                            : overallStatus === 'tidak_lulus' ? 'fail'
                            : overallStatus === 'selesai_tes' ? 'selesai'
                            : overallStatus === 'expired' ? 'expired'
                            : 'active';

                          return (
                          <tr key={`${s.candidate_id}-${s.id || 'none'}`} className={selected?.id === s.id ? 'row-selected' : ''}>
                            <td>{((candidatePage - 1) * effectiveCandidatePerPage) + idx + 1}</td>
                            <td>
                              {s.photo ? (
                                <img
                                  src={buildUploadUrl('face', s.photo)}
                                  alt={`Foto sesi ${s.candidate_name}`}
                                  style={{ width: '64px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)' }}
                                />
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>{s.candidate_name}</td>
                            <td>{s.candidate_email}</td>
                            <td>{s.position || '-'}</td>
                            <td>
                              <span className={`badge ${badgeClass}`}>
                              {overallStatus === 'lulus' ? '✅ Lulus'
                                : overallStatus === 'tidak_lulus' ? '❌ Tidak Lulus'
                                : overallStatus === 'selesai_tes' ? '🏁 Selesai Tes'
                                : overallStatus === 'expired' ? '⏰ Expired'
                                : '🟡 In Progress'}
                              </span>
                            </td>
                            <td>{overallStatus === 'in_progress' ? '-' : `${scoreValue}%`}</td>
                            <td className={(s.violation_count || 0) > 0 ? 'text-red' : ''}>{s.violation_count || 0}</td>
                            <td className="td-actions-row">
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                              <button className="btn-primary" onClick={() => openCandidateDetail(s)}>
                                Detail
                              </button>
                              <button className="btn-secondary" onClick={() => editCandidate(s)}>
                                Edit
                              </button>
                              <button className="btn-danger" onClick={() => deleteCandidateRow(s.candidate_id)}>
                                Hapus
                              </button>
                              <button
                                className="btn-info"
                                onClick={() => sendCandidateResultEmail(s, 'lulus_dengan_undangan')}
                                style={{ fontSize: '12px', padding: '5px 8px', whiteSpace: 'nowrap' }}
                              >
                                ✅ Lulus
                              </button>
                              <button
                                className="btn-danger"
                                onClick={() => sendCandidateResultEmail(s, 'tidak_lulus')}
                                disabled={!s.ready_to_send}
                                title={!s.ready_to_send ? 'Kandidat belum menyelesaikan semua modul' : ''}
                                style={{ fontSize: '12px', padding: '5px 8px', whiteSpace: 'nowrap' }}
                              >
                                ❌ Tidak Lulus
                              </button>
                            </div>
                          </td>
                          </tr>
                          );
                        })}
                        {paginatedCandidates.length === 0 && (
                          <tr><td colSpan="9" className="empty-row">Tidak ada data</td></tr>
                        )}
                      </tbody>
                    </table>

                    {totalCandidatePages > 1 && (
                      <div className="pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => setCandidatePage(Math.max(1, candidatePage - 1))}
                          disabled={candidatePage === 1}
                        >
                          ← Sebelumnya
                        </button>
                        <div className="pagination-info">
                          Halaman {candidatePage} dari {totalCandidatePages} (Total: {filtered.length} peserta)
                        </div>
                        <button
                          className="pagination-btn"
                          onClick={() => setCandidatePage(Math.min(totalCandidatePages, candidatePage + 1))}
                          disabled={candidatePage === totalCandidatePages}
                        >
                          Berikutnya →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {showCandidateDetailModal && (
                <div className="form-modal" onClick={() => setShowCandidateDetailModal(false)}>
                  <div className="form-box large modal-box" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Detail Kandidat</h3>
                      <button className="modal-close-btn" onClick={() => setShowCandidateDetailModal(false)}>✕</button>
                    </div>

                    {candidateDetailLoading ? (
                      <div className="loading-text">Memuat detail kandidat...</div>
                    ) : candidateDetail ? (
                      <div className="modal-body">

                        {/* === Info Kandidat === */}
                        {/* <div className="candidate-info-card">
                          <div className="detail-grid">
                            <div>
                              <p className="detail-label">Nama</p>
                              <p className="detail-value">{candidateDetail.candidate.name}</p>
                            </div>
                            <div>
                              <p className="detail-label">Email</p>
                              <p className="detail-value">{candidateDetail.candidate.email}</p>
                            </div>
                            <div>
                              <p className="detail-label">Posisi</p>
                              <p className="detail-value">{candidateDetail.candidate.position || '-'}</p>
                            </div>
                            
                          </div>
                        </div> */}

                        {/* === Info Kandidat === */}
<div className="candidate-info-card">
  <div className="detail-grid">
    <div>
      <p className="detail-label">Nama</p>
      <p className="detail-value">{candidateDetail.candidate.name}</p>
    </div>
    <div>
      <p className="detail-label">Email</p>
      <p className="detail-value">{candidateDetail.candidate.email}</p>
    </div>
    <div>
      <p className="detail-label">Posisi</p>
      <p className="detail-value">{candidateDetail.candidate.position || '-'}</p>
    </div>
  </div>
</div>

{/* === Profil Kandidat === */}
<div className="section-block">
  <div className="section-header-row">
    <h4>Profil Kandidat</h4>

    <button
      type="button"
      className={`toggle-section-btn ${showProfileSection ? 'open' : ''}`}
      onClick={() => setShowProfileSection((prev) => !prev)}
    >
      {showProfileSection ? 'Sembunyikan' : 'Tampilkan Profil'}
    </button>
  </div>

  {showProfileSection && (
    candidateDetail.profile ? (
      <div className="detail-grid">
        <div>
          <p className="detail-label">Nama Lengkap</p>
          <p className="detail-value">{candidateDetail.profile.name || '-'}</p>
        </div>
        <div>
          <p className="detail-label">Jenis Kelamin</p>
          <p className="detail-value">{candidateDetail.profile.gender || '-'}</p>
        </div>
        <div>
          <p className="detail-label">Nomor Telepon</p>
          <p className="detail-value">{candidateDetail.profile.phone || '-'}</p>
        </div>
        <div>
          <p className="detail-label">Tanggal Lahir</p>
          <p className="detail-value">
            {candidateDetail.profile.tgl_lahir
              ? new Date(candidateDetail.profile.tgl_lahir).toLocaleDateString('id-ID')
              : '-'}
          </p>
        </div>
        <div>
          <p className="detail-label">Suku</p>
          <p className="detail-value">{candidateDetail.profile.suku || '-'}</p>
        </div>
        <div>
          <p className="detail-label">Agama</p>
          <p className="detail-value">{candidateDetail.profile.agama || '-'}</p>
        </div>
        <div>
          <p className="detail-label">Golongan Darah</p>
          <p className="detail-value">{candidateDetail.profile.gol_darah || '-'}</p>
        </div>
      </div>
    ) : (
      <div className="empty-state">Belum ada data profil kandidat.</div>
    )
  )}
</div>

                        {/* === Action Buttons === */}
                        <div className="bulk-actions candidate-actions">
                          <div className="bulk-actions-left">
                            <button type="button" className="btn-primary" onClick={downloadCandidateDetailPdf}>
                              📄 Download PDF Detail
                            </button>
                          
                            <button
                              type="button"
                              className="btn-danger"
                              onClick={resetCandidateExamData}
                              disabled={selectedExams.length === 0}
                              title={selectedExams.length === 0 ? 'Pilih minimal satu ujian dulu' : ''}
                            >
                              ♻ Reset Exam Terpilih ({selectedExams.length})
                            </button>
                            <button type="button" className="btn-danger" onClick={resetCandidatePsychotestData}>
                              ♻ Reset Psikotest
                            </button>
                          </div>
                        </div>

                        {/* === Riwayat Ujian === */}
                        <div className="section-block">
                          <div className="section-header-row">
                            <h4>Riwayat Ujian ({candidateDetail.exams.length})</h4>
                          </div>

                          {candidateDetail.exams.length === 0 ? (
                            <div className="empty-state">Belum ada riwayat ujian</div>
                          ) : (
                            <>
                              <div className="bulk-actions" style={{ marginBottom: '10px' }}>
                                <div className="bulk-actions-left">
                                  <label className="upload-label">
                                    Show List:
                                    <select value={detailExamPerPage} onChange={(e) => setDetailExamPerPage(e.target.value)}>
                                      <option value="10">10</option>
                                      <option value="25">25</option>
                                      <option value="50">50</option>
                                      <option value="all">All</option>
                                    </select>
                                  </label>
                                  <button type="button" className="btn-info" onClick={exportCandidateExamHistoryExcel}>
                                    ⬇️ Export Excel
                                  </button>
                                </div>
                              </div>

                              <div className="sessions-table">
                                <table>
                                  <thead>
                                    <tr>
                                      <th><input                          type="checkbox"
                                              checked={candidateDetail.exams.length > 0 && selectedExams.length === candidateDetail.exams.filter(e => e.session_id).length}
                                              onChange={toggleSelectAllExams}
                                            /></th>
                                      <th>No</th>
                                      <th>Ujian</th>
                                      <th>Foto</th>
                                      <th>Status</th>
                                      <th>Nilai</th>
                                      <th>Pelanggaran</th>
                                      <th>Waktu</th>
                                      <th>Aksi</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedCandidateDetailExams.map((exam, idx) => {
                                      const statusLabel = exam.is_disqualified
                                        ? 'Diskualifikasi'
                                        : exam.status === 'active'
                                        ? 'Sedang Ujian'
                                        : exam.status === 'finished'
                                        ? 'Selesai'
                                        : exam.status;

                                      const statusClass = exam.is_disqualified
                                        ? 'status-danger'
                                        : exam.status === 'active'
                                        ? 'status-warning'
                                        : exam.status === 'finished'
                                        ? 'status-done'
                                        : 'status-neutral';

                                      return (
                                        <tr key={exam.session_id}>
                                          <td>
                                            <input
                                              type="checkbox"
                                              checked={selectedExams.includes(exam.session_id)}
                                              onChange={() => toggleExamSelection(exam.session_id)}
                                            />
                                          </td>
                                          <td>{((detailExamPage - 1) * effectiveDetailExamPerPage) + idx + 1}</td>
                                          <td>{exam.exam_title}</td>
                                          <td>
                                            {exam.session_photo ? (
                                              <img
                                                src={buildUploadUrl('face', exam.session_photo)}
                                                alt={`Foto sesi ${exam.exam_title}`}
                                                className="thumb-photo"
                                              />
                                            ) : (
                                              '-'
                                            )}
                                          </td>
                                          <td>
                                            <span className={`status-pill ${statusClass}`}>{statusLabel}</span>
                                          </td>
                                          <td>{exam.score != null ? `${exam.score}%` : '-'}</td>
                                          <td>{exam.violation_count || 0}</td>
                                          <td>{exam.started_at ? new Date(exam.started_at).toLocaleString('id-ID') : '-'}</td>
                                          <td>
                                            <button
                                              type="button"
                                              className="btn-secondary"
                                              onClick={() => openSessionAnswerDetail(exam)}
                                              disabled={!exam.session_id}
                                            >
                                              Detail Jawaban
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>

                                {totalDetailExamPages > 1 && (
                                  <div className="pagination" style={{ marginTop: '10px' }}>
                                    <button
                                      className="pagination-btn"
                                      onClick={() => setDetailExamPage(Math.max(1, detailExamPage - 1))}
                                      disabled={detailExamPage === 1}
                                    >
                                      ← Sebelumnya
                                    </button>
                                    <div className="pagination-info">
                                      Halaman {detailExamPage} dari {totalDetailExamPages} (Total: {candidateDetailExams.length} riwayat)
                                    </div>
                                    <button
                                      className="pagination-btn"
                                      onClick={() => setDetailExamPage(Math.min(totalDetailExamPages, detailExamPage + 1))}
                                      disabled={detailExamPage === totalDetailExamPages}
                                    >
                                      Berikutnya →
                                    </button>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>

                        {/* === Riwayat Psikotest === */}
                        <div className="section-block">
                          <h4>Riwayat Psikotest ({candidateDetail.psychotest_attempts.length})</h4>
                          {candidateDetail.psychotest_attempts.length === 0 ? (
                            <div className="empty-state">Belum ada riwayat psikotest</div>
                          ) : (
                            <div className="sessions-table">
                              <table>
                                <thead>
                                  <tr>
                                    <th>No</th>
                                    <th>Foto</th>
                                    <th>Status</th>
                                    <th>Mulai</th>
                                    <th>Selesai</th>
                                    <th>Hasil</th>
                                    <th>Aksi</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {candidateDetail.psychotest_attempts.map((attempt, idx) => {
                                    const statusLabel =
                                      attempt.status === 'completed'
                                        ? 'Selesai'
                                        : attempt.status === 'active'
                                          ? 'Sedang Berlangsung'
                                          : attempt.status === 'timeout'
                                            ? 'Timeout'
                                            : attempt.status === 'disqualified'
                                              ? 'Diskualifikasi'
                                              : attempt.status;

                                    const resultSummary = attempt.status === 'completed' && attempt.result
                                      ? formatPsychotestResult(attempt.result).summary
                                      : attempt.result
                                        ? 'Hasil tersedia'
                                        : 'Belum ada hasil';

                                    return (
                                      <tr key={attempt.id}>
                                        <td>{idx + 1}</td>
                                        <td>
                                          {attempt.photo ? (
                                            <img
                                              src={buildUploadUrl('tmp', attempt.photo)}
                                              alt="Foto awal psikotest"
                                              className="thumb-photo"
                                            />
                                          ) : (
                                            '-'
                                          )}
                                        </td>
                                        <td>{statusLabel}</td>
                                        <td>{attempt.started_at ? new Date(attempt.started_at).toLocaleString('id-ID') : '-'}</td>
                                        <td>{attempt.completed_at ? new Date(attempt.completed_at).toLocaleString('id-ID') : '-'}</td>
                                        <td>{resultSummary}</td>
                                        <td>
                                          <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() => openPsychotestAttemptDetail(attempt)}
                                          >
                                            Detail
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="empty-state">Detail kandidat tidak tersedia</div>
                    )}
                  </div>
                </div>
              )}

              {showPsychotestDetailModal && psychotestDetail && (
                <div className="form-modal" onClick={closePsychotestDetailModal}>
                  <div className="form-box large modal-box" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Detail Psikotest</h3>
                      <button className="modal-close-btn" onClick={closePsychotestDetailModal}>✕</button>
                    </div>

                    <div className="detail-grid">
                      <div>
                        <p className="detail-label">Status</p>
                        <p className="detail-value">
                          {psychotestDetail.status === 'completed'
                            ? 'Selesai'
                            : psychotestDetail.status === 'active'
                              ? 'Sedang Berlangsung'
                              : psychotestDetail.status === 'timeout'
                                ? 'Timeout'
                                : psychotestDetail.status === 'disqualified'
                                  ? 'Diskualifikasi'
                                  : psychotestDetail.status}
                        </p>
                      </div>
                      <div>
                        <p className="detail-label">Mulai</p>
                        <p className="detail-value">{psychotestDetail.started_at ? new Date(psychotestDetail.started_at).toLocaleString('id-ID') : '-'}</p>
                      </div>
                      <div>
                        <p className="detail-label">Selesai</p>
                        <p className="detail-value">{psychotestDetail.completed_at ? new Date(psychotestDetail.completed_at).toLocaleString('id-ID') : '-'}</p>
                      </div>
                      <div>
                        <p className="detail-label">Ringkasan</p>
                        <p className="detail-value">{psychotestDetail.result ? formatPsychotestResult(psychotestDetail.result).summary : '-'}</p>
                      </div>
                    </div>

              {psychotestGroups.length > 0 && (
                <div className="section-block">
                  <div className="bulk-actions" style={{ marginBottom: '12px', marginTop: '12px' }}>
                      <div className="bulk-actions-left">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: showPsychotestGroups ? '12px' : 0,
                    }}
                  >
                    {/* <h4 style={{ margin: 0 }}>Ringkasan Hasil Psikotest</h4> */}

                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setShowPsychotestGroups((previous) => !previous)}
                      style={{ whiteSpace: 'nowrap', backgroundColor: showPsychotestGroups ? '#e5e7eb' : '#0d7a7a', color: showPsychotestGroups ? '#000' : '#fff' }}
                    >
                      {showPsychotestGroups ? 'Ringkasan Hasil Psikotest' : 'Ringkasan Hasil Psikotest'}
                    </button>
                  </div>
                  </div>
                  </div>

                  {showPsychotestGroups && (
                  <div className="sessions-table" style={{ overflowX: 'auto' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '13px',
                      }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: '#0d7a7a' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', color: '#fff' }}>
                            Kategori
                          </th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', color: '#fff' }}>
                            Dimensi
                          </th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', color: '#fff' }}>
                            Kecenderungan
                          </th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', color: '#fff' }}>
                            Skor
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {psychotestGroups.map((group, groupIndex) => {
                          const levelOrder = {
                            tinggi: 0,
                            'sedang-tinggi': 1,
                            sedang: 2,
                            'sedang-rendah': 3,
                            rendah: 4,
                          };

                          const sorted = [...(group.members || [])].sort(
                            (a, b) =>
                              (levelOrder[a.level] ?? 5) - (levelOrder[b.level] ?? 5)
                          );

                          const highMembers = sorted.filter((member) => Number(member.score) >= 6);
                          const middleMembers = sorted.filter(
                            (member) =>
                              Number(member.score) >= 3.5 && Number(member.score) < 6
                          );
                          const lowMembers = sorted.filter(
                            (member) => Number(member.score) < 3.5
                          );

                          const groupColor =
                            group.label === 'HIGH ANALISYS'
                              ? '#dcfce7'
                              : group.label === 'MIDDLE RANGE'
                                ? '#fef9c3'
                                : '#fee2e2';

                          const groupTextColor =
                            group.label === 'HIGH ANALISYS'
                              ? '#166534'
                              : group.label === 'MIDDLE RANGE'
                                ? '#854d0e'
                                : '#991b1b';

                          const rowBg = groupIndex % 2 === 0 ? '#f8fafc' : '#fff';
                          const totalRows = sorted.length;

                          const sections = [
                            {
                              label: 'HIGH ANALISYS',
                              members: highMembers,
                              bg: '#dcfce7',
                              color: '#166534',
                            },
                            {
                              label: 'MIDDLE RANGE',
                              members: middleMembers,
                              bg: '#fef9c3',
                              color: '#854d0e',
                            },
                            {
                              label: 'LOW ANALISYS',
                              members: lowMembers,
                              bg: '#fee2e2',
                              color: '#991b1b',
                            },
                          ].filter((section) => section.members.length > 0);

                          let firstRowInGroup = true;

                          return sections.flatMap((section) =>
                            section.members.map((member, memberIndex) => {
                              const isFirstInGroup = firstRowInGroup;
                              firstRowInGroup = false;

                              const isFirstInSection = memberIndex === 0;

                              return (
                                <tr
                                  key={`${group.key}-${section.label}-${member.key}`}
                                  style={{
                                    backgroundColor: rowBg,
                                    borderBottom: '1px solid #e2e8f0',
                                  }}
                                >
                                  {isFirstInGroup && (
                                    <td
                                      rowSpan={totalRows}
                                      style={{
                                        padding: '8px 12px',
                                        fontWeight: 700,
                                        verticalAlign: 'top',
                                        borderRight: '2px solid #e2e8f0',
                                        backgroundColor: groupColor,
                                        color: groupTextColor,
                                        fontSize: '12px',
                                      }}
                                    >
                                      <div>{group.title}</div>

                                      <div
                                        style={{
                                          display: 'inline-block',
                                          marginTop: '6px',
                                          padding: '2px 6px',
                                          borderRadius: '4px',
                                          backgroundColor: groupColor,
                                          border: `1px solid ${groupTextColor}`,
                                          fontSize: '11px',
                                        }}
                                      >
                                        {group.label}
                                      </div>

                                      <div
                                        style={{
                                          marginTop: '4px',
                                          fontSize: '11px',
                                          opacity: 0.8,
                                        }}
                                      >
                                        Rata-rata: {group.averageScore}
                                      </div>
                                    </td>
                                  )}

                                  <td
                                    style={{
                                      padding: '7px 12px',
                                      fontWeight: isFirstInSection ? 600 : 400,
                                      borderRight: '1px solid #e2e8f0',
                                      fontSize: '12px',
                                    }}
                                  >
                                    {isFirstInSection && (
                                      <span
                                        style={{
                                          display: 'inline-block',
                                          marginBottom: '4px',
                                          padding: '1px 6px',
                                          borderRadius: '10px',
                                          backgroundColor: section.bg,
                                          color: section.color,
                                          border: `1px solid ${section.color}`,
                                          fontSize: '10px',
                                          fontWeight: 700,
                                        }}
                                      >
                                        {section.label}
                                      </span>
                                    )}

                                    <div>{member.title}</div>
                                  </td>

                                  <td
                                    style={{
                                      padding: '7px 12px',
                                      color: '#374151',
                                      borderRight: '1px solid #e2e8f0',
                                      fontSize: '12px',
                                      lineHeight: '1.4',
                                    }}
                                  >
                                    {member.tendency}
                                  </td>

                                  <td
                                    style={{
                                      padding: '7px 12px',
                                      textAlign: 'center',
                                      fontWeight: 700,
                                      color:
                                        Number(member.score) >= 6
                                          ? '#166534'
                                          : Number(member.score) >= 3.5
                                            ? '#854d0e'
                                            : '#991b1b',
                                      fontSize: '13px',
                                    }}
                                  >
                                    {member.score}/9
                                  </td>
                                </tr>
                              );
                            })
                          );
                        })}
                      </tbody>
                  </table>
                    </div>
                  )}
                </div>
              )}
                    <div className="bulk-actions" style={{ marginBottom: '12px', marginTop: '12px' }}>
                      <div className="bulk-actions-left">
                        <button type="button" className="btn-primary" onClick={downloadPsychotestDetailPdf}>
                          📄 Download PDF Detail Psikotest
                        </button>
                      </div>
                    </div>

                    {psychotestDetail.answers?.length > 0 ? (
                      <div className="sessions-table" style={{ marginTop: '16px' }}>
                        <table>
                          <thead>
                            <tr>
                              <th>No</th>
                              <th>Pertanyaan A</th>
                              <th>Pertanyaan B</th>
                              <th>Jawaban</th>
                            </tr>
                          </thead>
                          <tbody>
                            {psychotestDetail.answers.map((answer, idx) => (
                              <tr key={`${psychotestDetail.id}-${answer.question_id}`}>
                                <td>{idx + 1}</td>
                                <td>
                                  <div style={{ fontWeight: 600 }}>{answer.dimension_a || '-'}</div>
                                  <div className="text-sm">{answer.statement_a || '-'}</div>
                                </td>
                                <td>
                                  <div style={{ fontWeight: 600 }}>{answer.dimension_b || '-'}</div>
                                  <div className="text-sm">{answer.statement_b || '-'}</div>
                                </td>
                                <td>
                                  {String(answer.answer_value || '').toLowerCase() === 'a'
                                    ? 'A'
                                    : String(answer.answer_value || '').toLowerCase() === 'b'
                                      ? 'B'
                                      : String(answer.answer_value || '-').toUpperCase()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="empty-state">Detail jawaban psikotest belum tersedia</div>
                    )}
                  
                        <div className="section-block">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <h4>Pelanggaran ({psychotestDetail.violations?.length || 0})</h4>
                            {psychotestDetail.violations?.length > 0 && (
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <label style={{ fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedViolations.size === psychotestDetail.violations.length}
                                    onChange={toggleSelectAllPsychotestViolations}
                                  />
                                  Pilih Semua
                                </label>
                                {selectedViolations.size > 0 && (
                                  <button
                                    onClick={deleteSelectedPsychotestViolations}
                                    style={{
                                      padding: '5px 12px',
                                      backgroundColor: '#ef4444',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    🗑 Hapus ({selectedViolations.size})
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {psychotestDetail.violations?.length > 0 ? (
                            <div className="detail-list">
                              {psychotestDetail.violations.map((violation) => (
                                <div
                          key={violation.id}
                          className="detail-item"
                          style={{
                            backgroundColor: selectedViolations.has(violation.id) ? '#fef2f2' : '',
                            border: selectedViolations.has(violation.id) ? '1px solid #fca5a5' : '',
                            borderRadius: '6px',
                          }}
                        >
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', width: '100%' }}>
                            <input
                              type="checkbox"
                              checked={selectedViolations.has(violation.id)}
                              onChange={() => toggleViolationSelect(violation.id)}
                              style={{ marginTop: '4px', flexShrink: 0, cursor: 'pointer' }}
                            />

                                    {violation.photo_path && (
                                      <img
                                        src={buildUploadUrl('proof', violation.photo_path)}
                                        alt="Foto pelanggaran"
                                        style={{ width: '90px', height: '68px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)', flexShrink: 0 }}
                                      />
                                    )}
                                    <div style={{ flex: 1 }}>
                                      <strong>{VIOLATION_LABELS[violation.violation_type] || violation.violation_type || '-'}</strong>
                                      <p className="text-sm">{violation.description || '-'}</p>
                                    </div>
                                  </div>
                                  <div className="detail-item-right">
                                    <span>{violation.logged_at ? new Date(violation.logged_at).toLocaleString('id-ID') : '-'}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="empty-state">Tidak ada pelanggaran tercatat</div>
                          )}
                        </div>
                   
                  </div>

                </div>
              )}

              {showSessionDetailModal && (
                <div className="form-modal" onClick={() => setShowSessionDetailModal(false)}>
                  <div className="form-box large modal-box" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Detail Jawaban Ujian</h3>
                      <button className="modal-close-btn" onClick={() => setShowSessionDetailModal(false)}>✕</button>
                    </div>

                    {sessionDetailLoading ? (
                      <div className="loading-text">Memuat detail jawaban...</div>
                    ) : sessionDetail ? (
                      <div className="modal-body">
                        <div className="bulk-actions" style={{ marginBottom: '12px' }}>
                          <div className="bulk-actions-left">
                            <button type="button" className="btn-primary" onClick={downloadSessionDetailPdf}>
                              📄 Download PDF Detail Jawaban
                            </button>
                          </div>
                        </div>

                        <div className="detail-grid">
                          <div>
                            <p className="detail-label">Nama</p>
                            <p className="detail-value">{sessionDetail.session?.candidate_name || '-'}</p>
                          </div>
                          <div>
                            <p className="detail-label">Email</p>
                            <p className="detail-value">{sessionDetail.session?.candidate_email || '-'}</p>
                          </div>
                          <div>
                            <p className="detail-label">Ujian</p>
                            <p className="detail-value">{sessionDetail.session?.exam_title || '-'}</p>
                          </div>
                          <div>
                            <p className="detail-label">Status</p>
                            <p className="detail-value">{sessionDetail.session?.status || '-'}</p>
                          </div>
                          <div>
                            <p className="detail-label">Skor</p>
                            <p className="detail-value">{sessionDetail.session?.score != null ? `${sessionDetail.session.score}%` : '-'}</p>
                          </div>
                          <div>
                            <p className="detail-label">Foto Sesi</p>
                            <p className="detail-value">{sessionDetail.session?.photo ? 'Ada' : 'Tidak ada'}</p>
                          </div>
                        </div>

                        {sessionDetail.session?.photo && (
                          <div className="section-block">
                            <h4>Foto Saat Ujian</h4>
                            <div className="preview-box" style={{ maxWidth: '320px' }}>
                              <img
                                src={buildUploadUrl('face', sessionDetail.session.photo)}
                                alt="Foto sesi ujian"
                                style={{ width: '100%', display: 'block', borderRadius: '8px' }}
                              />
                            </div>
                          </div>
                        )}

                        <div className="section-block">
                          <h4>Jawaban Kandidat ({sessionDetail.answers?.length || 0})</h4>
                          {sessionDetail.answers?.length > 0 ? (
                            <div className="questions-table-container">
                              <table className="questions-table">
                                <thead>
                                  <tr>
                                    <th style={{ width: '50px' }}>No</th>
                                    <th>Pertanyaan</th>
                                    <th style={{ width: '90px' }}>Foto</th>
                                    <th>Jawaban Kandidat</th>
                                    <th style={{ width: '90px' }}>Kunci</th>
                                    <th style={{ width: '90px' }}>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sessionDetail.answers.map((answer, idx) => (
                                    <tr key={`${answer.question_id}-${idx}`}>
                                      <td>{idx + 1}</td>
                                      <td>{answer.question_text || '-'}</td>
                                      <td>
                                        {answer.photo ? (
                                          <img
                                            src={buildUploadUrl('questions', answer.photo)}
                                            alt="Foto soal"
                                            style={{ width: '72px', height: '54px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)' }}
                                          />
                                        ) : (
                                          '-'
                                        )}
                                      </td>
                                      <td>{answer.correct_answer ? ((answer.selected_option || '-').toString().toUpperCase()) : (answer.selected_option || '-')}</td>
                                      <td>{answer.correct_answer ? String(answer.correct_answer).toUpperCase() : '-'}</td>
                                      <td>{answer.correct_answer ? (Number(answer.is_correct) === 1 ? 'Benar' : 'Salah') : 'Essay'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="empty-state">Belum ada jawaban tersimpan</div>
                          )}
                        </div>

                        <div className="section-block">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <h4>Pelanggaran ({sessionDetail.violations?.length || 0})</h4>
                            {sessionDetail.violations?.length > 0 && (
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {selectedViolations.size > 0 && (
                                  <button
                                    onClick={deleteSelectedExamViolations}
                                    style={{
                                      padding: '5px 12px',
                                      backgroundColor: '#ef4444',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    🗑 Hapus ({selectedViolations.size})
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          {sessionDetail.violations?.length > 0 ? (
                            <div className="detail-list">
                              {sessionDetail.violations.map((violation) => (
                                <div key={violation.id} className="detail-item">
                                  <input
      type="checkbox"
      checked={selectedViolations.has(violation.id)}
      onChange={() => toggleViolationSelect(violation.id)}
      style={{ marginTop: '4px', flexShrink: 0, cursor: 'pointer' }}
    />
                                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', width: '100%' }}>
                                  {violation.photo_path && (
              <img
                src={buildUploadUrl('proof', violation.photo_path)}
                alt="Foto pelanggaran"
                style={{ width: '90px', height: '68px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)', flexShrink: 0 }}
              />
            )}
                                  <div style={{ flex: 1 }}>
                                    <strong>{VIOLATION_LABELS[violation.violation_type] || violation.violation_type || '-'}</strong>
                                    <p className="text-sm">{violation.description || '-'}</p>
                                  </div>
                                  </div>
                                  <div className="detail-item-right">
                                    <span>{violation.logged_at ? new Date(violation.logged_at).toLocaleString('id-ID') : '-'}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="empty-state">Tidak ada pelanggaran tercatat</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="empty-state">Detail jawaban tidak tersedia</div>
                    )}
                  </div>
                </div>
              )}

              {/* === Schedule Management Modal === */}
              {showScheduleModal && (
                <div className="form-modal" onClick={closeScheduleModal}>
                  <div className="form-box large modal-box" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Kelola Jadwal Peserta</h3>
                      <button className="modal-close-btn" onClick={closeScheduleModal}>✕</button>
                    </div>

                    <div className="modal-body">
                      <div className="bulk-actions" style={{ marginBottom: '16px', backgroundColor: '#f0f4f8', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.4' }}>
                          ℹ️ <strong>Catatan:</strong> Psikotest harus urutan pertama (nomor 1). Anda bisa mengatur waktu mulai dan selesai untuk setiap modul. Modul yang sedang berlangsung atau sudah selesai tidak bisa diedit urutan dan waktunya.
                        </div>
                      </div>

                      <div className="sessions-table">
                        <table>
                          <thead>
                            <tr>
                              <th style={{ width: '50px' }}>No</th>
                              <th>Modul</th>
                              <th>Status</th>
                              <th>Waktu Mulai</th>
                              <th>Waktu Selesai</th>
                              <th style={{ width: '120px' }}>Aksi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {scheduleModules.map((module, idx) => {
                              const canEdit = canEditSchedule(module);
                              const isEditing = editingModuleIndex === idx;
                              
                              return (
                                <tr key={module.module_key} className={!canEdit ? 'row-disabled' : ''}>
                                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{module.sequence_no}</td>
                                  <td>
                                    <div style={{ fontWeight: '600' }}>{module.title}</div>
                                    {module.description && <div style={{ fontSize: '12px', color: '#999' }}>{module.description}</div>}
                                  </td>
                                  <td>
                                    <span className={`status-pill status-${
                                      module.module_status === 'active' ? 'warning' :
                                      module.module_status === 'completed' ? 'done' :
                                      module.module_status === 'pending' ? 'neutral' :
                                      'danger'
                                    }`}>
                                      {module.module_status === 'active' ? 'Sedang Berlangsung' :
                                       module.module_status === 'completed' ? 'Selesai' :
                                       module.module_status === 'pending' ? 'Belum Dimulai' :
                                       module.module_status === 'failed' ? 'Gagal' :
                                       module.module_status === 'disqualified' ? 'Diskualifikasi' :
                                       module.module_status}
                                    </span>
                                  </td>
                                  <td>
                                    {isEditing ? (
                                      <input
                                        type="datetime-local"
                                        value={editingModuleTime.starts_at}
                                        onChange={(e) => setEditingModuleTime({ ...editingModuleTime, starts_at: e.target.value })}
                                        style={{ fontSize: '12px', padding: '6px' }}
                                      />
                                    ) : (
                                      <span>{module.starts_at ? new Date(module.starts_at).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</span>
                                    )}
                                  </td>
                                  <td>
                                    {isEditing ? (
                                      <input
                                        type="datetime-local"
                                        value={editingModuleTime.ends_at}
                                        onChange={(e) => setEditingModuleTime({ ...editingModuleTime, ends_at: e.target.value })}
                                        style={{ fontSize: '12px', padding: '6px' }}
                                      />
                                    ) : (
                                      <span>{module.ends_at ? new Date(module.ends_at).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</span>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    {isEditing ? (
                                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                        <button
                                          type="button"
                                          className="btn-primary"
                                          onClick={saveModuleTime}
                                          style={{ fontSize: '11px', padding: '4px 8px' }}
                                        >
                                          💾 Simpan
                                        </button>
                                        <button
                                          type="button"
                                          className="btn-secondary"
                                          onClick={cancelEditModuleTime}
                                          style={{ fontSize: '11px', padding: '4px 8px' }}
                                        >
                                          ✕ Batal
                                        </button>
                                      </div>
                                    ) : (
                                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                        <button
                                          type="button"
                                          className="btn-secondary"
                                          onClick={() => startEditModuleTime(idx)}
                                          disabled={!canEdit}
                                          style={{ fontSize: '11px', padding: '4px 6px' }}
                                          title={!canEdit ? `Tidak bisa diedit (status: ${module.module_status})` : 'Edit waktu'}
                                        >
                                          ✏️ Waktu
                                        </button>
                                        <button
                                          type="button"
                                          className="btn-secondary"
                                          onClick={() => moveModuleUp(idx)}
                                          disabled={!canEdit || idx === 0}
                                          style={{ fontSize: '11px', padding: '4px 6px' }}
                                          title="Naik urutan"
                                        >
                                          ↑
                                        </button>
                                        <button
                                          type="button"
                                          className="btn-secondary"
                                          onClick={() => moveModuleDown(idx)}
                                          disabled={!canEdit || idx === scheduleModules.length - 1}
                                          style={{ fontSize: '11px', padding: '4px 6px' }}
                                          title="Turun urutan"
                                        >
                                          ↓
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="bulk-actions" style={{ marginTop: '16px' }}>
                        <div className="bulk-actions-left">
                          <button
                            type="button"
                            className="btn-primary"
                            onClick={saveScheduleChanges}
                            disabled={savingSchedule}
                          >
                            {savingSchedule ? '💾 Menyimpan...' : '💾 Simpan Jadwal'}
                          </button>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={closeScheduleModal}
                            disabled={savingSchedule}
                          >
                            ✕ Batal
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </>
        )}

        {/* ─── EXAMS TAB ──────────────────────────────────────────────────────*/}
        {activeTab === 'exams' && (
          <div className="exam-mgmt">
            <div className="mgmt-header">
              <h2>Kelola Ujian</h2>
              <button className="btn-add" onClick={() => {
                setEditingExam(null);
                setExamForm({ title: '', description: '', duration_minutes: 60, max_violations: 1, total: 0, multiple_choice: 1, passing_grade: 60, position: 'all' });
                setShowExamForm(true);
              }}>
                ➕ Tambah Ujian Baru
              </button>
            </div>

            {showExamForm && (
              <div className="form-modal">
                <div className="form-box">
                  <div className="modal-header">
                    <h3>{editingExam ? 'Edit Ujian' : 'Ujian Baru'}</h3>
                    <button className="modal-close-btn" onClick={() => setShowExamForm(false)}>✕</button>
                  </div>
                  <div className="form-group">
                    <label>Judul Ujian</label>
                    <input
                      type="text"
                      value={examForm.title}
                      onChange={(e) => setExamForm({...examForm, title: e.target.value})}
                      placeholder="Contoh: Tes Kemampuan Umum"
                    />
                  </div>
                  <div className="form-group">
                    <label>Deskripsi</label>
                    <textarea
                      value={examForm.description}
                      onChange={(e) => setExamForm({...examForm, description: e.target.value})}
                      placeholder="Deskripsi ujian..."
                      rows="3"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Durasi (menit)</label>
                      <input
                        type="number"
                        value={examForm.duration_minutes}
                        onChange={(e) => setExamForm({...examForm, duration_minutes: parseInt(e.target.value)})}
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Maks Pelanggaran</label>
                      <input
                        type="number"
                        value={examForm.max_violations}
                        onChange={(e) => setExamForm({...examForm, max_violations: parseInt(e.target.value)})}
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Posisi</label>
                      <select
                        value={examForm.position}
                        onChange={(e) => setExamForm({...examForm, position: e.target.value})}
                      >
                       <option value=""> Pilih Posisi </option>
                      {positions.map((p) => (
                          <option key={p.id} value={p.position_cd || p.position_name}>
                            {p.position_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Nilai Kelulusan Minimum (%)</label>
                    <input
                      type="number"
                      value={examForm.passing_grade}
                      onChange={(e) => setExamForm({...examForm, passing_grade: parseFloat(e.target.value)})}
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="Contoh: 60"
                    />
                  </div>
                  <div className="form-group">
                    <label>Total Soal Ditampilkan ke Kandidat</label>
                    <input
                      type="number"
                      value={examForm.total}
                      onChange={(e) => setExamForm({ ...examForm, total: Math.max(0, parseInt(e.target.value || '0', 10)) })}
                      min="0"
                      step="1"
                      placeholder="0 = tampilkan semua soal"
                    />
                    <small style={{ display: 'block', marginTop: '6px', color: 'var(--text-muted)' }}>
                      Isi 0 untuk menampilkan semua soal yang tersedia pada bank soal.
                    </small>
                  </div>
                  <div className="form-actions">
                    <button className="btn-save" onClick={saveExam}>Simpan</button>
                    <button className="btn-cancel" onClick={() => setShowExamForm(false)}>Batal</button>
                  </div>
                </div>
              </div>
            )}

            {loadingExams ? (
              <div className="loading-text">Memuat ujian...</div>
            ) : (
              <div className="exam-list">
                {exams.map(exam => (
                  <div key={exam.id} className="exam-item">
                    <div className="exam-info">
                      <h3>{exam.title}</h3>
                      <p className="desc">{exam.description}</p>
                      <div className="meta">
                        <span>⏱ {exam.duration_minutes} menit</span>
                        <span>❓ Tampil ke kandidat: {exam.total > 0 ? Math.min(exam.total, exam.total_questions) : exam.total_questions} soal</span>
                        <span>📚 Bank soal: {exam.total_questions} soal</span>
                        <span>{exam.multiple_choice === 1 ? '📝' : '✍️'} {exam.multiple_choice === 1 ? 'Pilihan Ganda' : 'Essay'}</span>
                        <span>⚠️ Max {exam.max_violations} pelanggaran</span>
                        <span>✅ Min Score: {exam.passing_grade}%</span>
                        <span>� Posisi: {exam.position === 'all' ? 'Semua Posisi' : exam.position}</span>
                        <span>�👤 Dibuat oleh: {exam.created_by_name}</span>
                      </div>
                    </div>
                    <div className="exam-actions">
                      <button className="btn-primary" onClick={() => openExamQuestionsModal(exam)}>
                        ▶ Lihat Soal
                      </button>
                      <button className="btn-secondary" onClick={() => startEditExam(exam)}>Edit</button>
                      <button className="btn-danger" onClick={() => deleteExam(exam.id)}>Hapus</button>
                    </div>

                    {/* Questions section */}
                    {expandedExams[exam.id] && selectedExamForQuestions === exam.id && (
                      <div className="questions-section">
                        <div className="questions-header">
                          <div>
                            <h4>Soal untuk: {exam.title}</h4>
                            <p style={{fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px'}}>
                              Tipe: {exam.multiple_choice === 1 ? '📝 Pilihan Ganda' : '✍️ Essay'}
                            </p>
                          </div>
                          <div style={{display: 'flex', gap: '8px'}}>
                            <button className="btn-info" onClick={() => { 
                              setUploadInstructionsType(exam.multiple_choice === 1 ? 'multiple-choice' : 'essay'); 
                              setShowUploadInstructions(true); 
                            }}>
                              ℹ️ Panduan Upload
                            </button>
                            <button className="btn-add-question" onClick={() => {
                              setEditingQuestion(null);
                              setQuestionForm({
                                question_text: '',
                                option_a: '',
                                option_b: '',
                                option_c: '',
                                option_d: '',
                                option_e: '',
                                correct_answer: 'a',
                                points: 10
                              });
                              setShowQuestionForm(true);
                            }}>
                              ➕ Tambah Soal
                            </button>
                          </div>
                        </div>

                        {showQuestionForm && selectedExamForQuestions === exam.id && (
                          <div className="form-modal">
                            <div className="form-box large">
                              <div className="modal-header">
                                <h3>
                                  {editingQuestion ? 'Edit Soal' : 'Soal Baru'}
                                  <span style={{fontSize: '13px', fontWeight: 400, color: 'var(--text-muted)', marginLeft: '10px'}}>
                                    {exam.multiple_choice === 1 ? '📝 Pilihan Ganda' : '✍️ Essay'}
                                  </span>
                                </h3>
                                <button className="modal-close-btn" onClick={() => setShowQuestionForm(false)}>✕</button>
                              </div>
                              <div className="form-group">
                                <label>Pertanyaan</label>
                                <textarea
                                  value={questionForm.question_text}
                                  onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                                  placeholder="Masukkan pertanyaan..."
                                  rows="3"
                                />
                              </div>

                              {exam.multiple_choice === 1 ? (
                                <>
                                  <div className="form-group">
                                    <label>Opsi A</label>
                                    <input type="text" value={questionForm.option_a} onChange={(e) => setQuestionForm({...questionForm, option_a: e.target.value})} placeholder="Opsi A" />
                                  </div>
                                  <div className="form-group">
                                    <label>Opsi B</label>
                                    <input type="text" value={questionForm.option_b} onChange={(e) => setQuestionForm({...questionForm, option_b: e.target.value})} placeholder="Opsi B" />
                                  </div>
                                  <div className="form-group">
                                    <label>Opsi C</label>
                                    <input type="text" value={questionForm.option_c} onChange={(e) => setQuestionForm({...questionForm, option_c: e.target.value})} placeholder="Opsi C" />
                                  </div>
                                  <div className="form-group">
                                    <label>Opsi D</label>
                                    <input type="text" value={questionForm.option_d} onChange={(e) => setQuestionForm({...questionForm, option_d: e.target.value})} placeholder="Opsi D" />
                                  </div>
                                  <div className="form-group">
                                    <label>Opsi E <span style={{fontWeight:400, color:'var(--text-muted)'}}>(opsional)</span></label>
                                    <input type="text" value={questionForm.option_e} onChange={(e) => setQuestionForm({...questionForm, option_e: e.target.value})} placeholder="Opsi E (kosongkan jika tidak ada)" />
                                  </div>
                                  <div className="form-row">
                                    <div className="form-group">
                                      <label>Jawaban Benar</label>
                                      <select value={questionForm.correct_answer} onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}>
                                        <option value="a">A</option>
                                        <option value="b">B</option>
                                        <option value="c">C</option>
                                        <option value="d">D</option>
                                        <option value="e">E</option>
                                      </select>
                                    </div>
                                    <div className="form-group">
                                      <label>Poin</label>
                                      <input type="number" value={questionForm.points} onChange={(e) => setQuestionForm({...questionForm, points: parseInt(e.target.value)})} min="1" />
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="essay-info-box">
                                    <span>✍️</span>
                                    <div>
                                      <strong>Soal Essay</strong>
                                      <p>Recruiter akan mengoreksi dan memberikan nilai secara manual setelah kandidat mengerjakan ujian.</p>
                                    </div>
                                  </div>
                                  <div className="form-group">
                                    <label>Rubrik Penilaian <span style={{fontWeight:400, color:'var(--text-muted)'}}>(panduan untuk recruiter)</span></label>
                                    <textarea
                                      value={questionForm.option_a}
                                      onChange={(e) => setQuestionForm({...questionForm, option_a: e.target.value})}
                                      placeholder="Contoh: Jawaban lengkap dan tepat: 100%, Sebagian benar: 50-70%, Tidak relevan: 0%"
                                      rows="3"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label>Poin Maksimal</label>
                                    <input type="number" value={questionForm.points} onChange={(e) => setQuestionForm({...questionForm, points: parseInt(e.target.value)})} min="1" />
                                  </div>
                                </>
                              )}

                              <div className="form-group">
                                <label>Foto Soal <span style={{fontWeight:400, color:'var(--text-muted)'}}>(opsional)</span></label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0] || null;
                                    setQuestionPhotoFile(file);
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onload = () => setQuestionPhotoPreview(reader.result);
                                      reader.readAsDataURL(file);
                                    } else {
                                      setQuestionPhotoPreview(null);
                                    }
                                  }}
                                />
                                {questionPhotoPreview && (
                                  <div className="preview-box">
                                    <img src={questionPhotoPreview} alt="Preview soal" />
                                  </div>
                                )}
                              </div>
                              <div className="form-actions">
                                <button className="btn-save" onClick={saveQuestion}>Simpan</button>
                                <button className="btn-cancel" onClick={() => setShowQuestionForm(false)}>Batal</button>
                              </div>
                            </div>
                          </div>
                        )}

                        {loadingQuestions ? (
                          <div className="loading-text">Memuat soal...</div>
                        ) : (
                          <div>
                            <div className="bulk-actions">
                              <div className="bulk-actions-left">
                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={selectAllQuestions}
                                    onChange={toggleSelectAllQuestions}
                                  />
                                  Pilih Semua
                                </label>
                                <button className="btn-danger" onClick={bulkDeleteSelectedQuestions}>Hapus Terpilih</button>
                              </div>
                            </div>

                            <div className="bulk-actions">
                              <label className="upload-label">
                                📤 Upload Excel soal:
                                <input
                                  type="file"
                                  accept=".xlsx,.xls"
                                  onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
                                />
                              </label>
                              <button className="btn-primary" onClick={uploadQuestionsExcelFile}>Upload Excel</button>
                            </div>

                            <div className="bulk-actions">
                              <div className="bulk-actions-left">
                                <a className="btn-info link-btn" href="/templates/Template_Exam_Pilihan_Ganda.xlsx" download>
                                  ⬇️ Template Exam Pilihan Ganda
                                </a>
                               
                                <label className="upload-label">
                                  Show List:
                                  <select value={questionPerPage} onChange={(e) => setQuestionPerPage(e.target.value)}>
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="all">All</option>
                                  </select>
                                </label>
                                <button type="button" className="btn-info" onClick={exportExamQuestionsExcel}>
                                  ⬇️ Export Excel
                                </button>
                              </div>
                            </div>

                            {questions.length === 0 ? (
                              <div className="empty-state">Tidak ada soal. Buat soal baru untuk ujian ini.</div>
                            ) : (
                              <div className="questions-table-container">
                                <table className="questions-table">
                                  <thead>
                                    <tr>
                                      <th style={{width: '30px'}}>
                                        <input
                                          type="checkbox"
                                          checked={selectAllQuestions}
                                          onChange={toggleSelectAllQuestions}
                                        />
                                      </th>
                                      <th style={{width: '50px'}}>No</th>
                                      <th>Pertanyaan</th>
                                      <th style={{width: '100px'}}>Tipe</th>
                                      <th>Opsi & Jawaban</th>
                                      <th style={{width: '60px'}}>Poin</th>
                                      <th style={{width: '80px'}}>Aksi</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedQuestions.map((q, idx) => (
                                      <tr key={q.id} className={selectedQuestionIds.includes(q.id) ? 'selected' : ''}>
                                        <td>
                                          <input
                                            type="checkbox"
                                            checked={selectedQuestionIds.includes(q.id)}
                                            onChange={(e) => toggleQuestionSelection(q.id, e.target.checked)}
                                          />
                                        </td>
                                        <td>{((questionPage - 1) * effectiveQuestionPerPage) + idx + 1}</td>
                                        <td>
                                          <div className="td-question">
                                            {q.photo && <span className="q-photo-badge">📸</span>}
                                            <span className="q-text-preview">{q.question_text.substring(0, 80)}{q.question_text.length > 80 ? '...' : ''}</span>
                                          </div>
                                        </td>
                                        <td>
                                          <span className={`badge badge-${q.correct_answer ? 'mc' : 'essay'}`}>
                                            {q.correct_answer ? '📝 Pilihan Ganda' : '✍️ Essay'}
                                          </span>
                                        </td>
                                        <td>
                                          {q.correct_answer ? (
                                            <div className="td-options">
                                              {['a', 'b', 'c', 'd', 'e'].map(opt => {
                                                const value = q[`option_${opt}`];
                                                if (!value) return null;
                                                return (
                                                  <div key={opt} className={`option-item ${q.correct_answer === opt ? 'correct-option' : ''}`}>
                                                    <strong>{opt.toUpperCase()}:</strong> {value.substring(0, 40)}{value.length > 40 ? '...' : ''}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : (
                                            <div>
                                              <span className="essay-label">✍️ Essay — Recruiter menilai</span>
                                              {q.option_a && (
                                                <div style={{fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px'}}>
                                                  📋 Rubrik: {q.option_a.substring(0, 60)}{q.option_a.length > 60 ? '...' : ''}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </td>
                                        <td><strong>{q.points}</strong></td>
                                        <td>
                                          <div className="td-actions">
                                            <button className="btn-sm btn-secondary" onClick={() => startEditQuestion(q)}>Edit</button>
                                            <button className="btn-sm btn-danger" onClick={() => deleteQuestion(q.id)}>Hapus</button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                  <div className="pagination">
                                    <button 
                                      className="pagination-btn"
                                      onClick={() => setQuestionPage(Math.max(1, questionPage - 1))}
                                      disabled={questionPage === 1}
                                    >
                                      ← Sebelumnya
                                    </button>
                                    <div className="pagination-info">
                                      Halaman {questionPage} dari {totalPages} (Total: {questions.length} soal)
                                    </div>
                                    <button 
                                      className="pagination-btn"
                                      onClick={() => setQuestionPage(Math.min(totalPages, questionPage + 1))}
                                      disabled={questionPage === totalPages}
                                    >
                                      Berikutnya →
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {exams.length === 0 && <div className="empty-state">Tidak ada ujian. Buat ujian baru untuk memulai.</div>}
              </div>
            )}
          </div>
        )}

        {activeTab === 'psychotest' && (
          <div className="exam-mgmt">
            <div className="mgmt-header">
              <h2>Kelola Soal Psikotest (A/B)</h2>
              <button className="btn-add" onClick={() => {
                resetPsychotestForm();
                setShowPsychotestForm(true);
              }}>
                ➕ Tambah Soal Psikotest
              </button>
            </div>

            {showPsychotestForm && (
              <div className="form-modal">
                <div className="form-box">
                  <div className="modal-header">
                    <h3>{editingPsychotestQuestion ? 'Edit Soal Psikotest' : 'Soal Psikotest Baru'}</h3>
                    <button className="modal-close-btn" onClick={() => setShowPsychotestForm(false)}>✕</button>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Dimensi A</label>
                      <select value={psychotestForm.dimension_a} onChange={(e) => setPsychotestForm({ ...psychotestForm, dimension_a: e.target.value })}>
                        {PSY_DIMENSIONS.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Dimensi B</label>
                      <select value={psychotestForm.dimension_b} onChange={(e) => setPsychotestForm({ ...psychotestForm, dimension_b: e.target.value })}>
                        {PSY_DIMENSIONS.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Pernyataan A</label>
                    <textarea rows="3" value={psychotestForm.statement_a} onChange={(e) => setPsychotestForm({ ...psychotestForm, statement_a: e.target.value })} />
                  </div>

                  <div className="form-group">
                    <label>Pernyataan B</label>
                    <textarea rows="3" value={psychotestForm.statement_b} onChange={(e) => setPsychotestForm({ ...psychotestForm, statement_b: e.target.value })} />
                  </div>

                  <div className="form-group">
                    <label>Status</label>
                    <select value={psychotestForm.is_active} onChange={(e) => setPsychotestForm({ ...psychotestForm, is_active: parseInt(e.target.value, 10) })}>
                      <option value={1}>Aktif</option>
                      <option value={0}>Nonaktif</option>
                    </select>
                  </div>

                  <div className="form-actions">
                    <button className="btn-save" onClick={savePsychotestQuestion}>Simpan</button>
                    <button className="btn-cancel" onClick={() => setShowPsychotestForm(false)}>Batal</button>
                  </div>
                </div>
              </div>
            )}

            <div className="bulk-actions" style={{ marginTop: '4px' }}>
              <div className="bulk-actions-left">
                <label className="upload-label">
                  ⏱ Durasi Psikotest (menit):
                  <input
                    type="number"
                    min="1"
                    max="180"
                    value={psychotestDurationMinutes}
                    onChange={(e) => setPsychotestDurationMinutes(parseInt(e.target.value || '0', 10))}
                    style={{ width: '110px' }}
                  />
                </label>
                <label className="upload-label">
                  🔢 Jumlah Soal Ditampilkan:
                  <input
                    type="number"
                    min="0"
                    max="500"
                    value={psychotestTotalQuestions}
                    onChange={(e) => setPsychotestTotalQuestions(parseInt(e.target.value || '0', 10))}
                    style={{ width: '120px' }}
                  />
                </label>
                <label className="upload-label">
                  ⚠️ Maks Pelanggaran:
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={psychotestMaxViolations}
                    onChange={(e) => setPsychotestMaxViolations(parseInt(e.target.value || '0', 10))}
                    style={{ width: '110px' }}
                  />
                </label>
                <button className="btn-primary" onClick={savePsychotestDuration} disabled={savingPsychotestDuration}>
                  {savingPsychotestDuration ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </div>

            <div className="bulk-actions" style={{ marginTop: '4px' }}>
              <div className="bulk-actions-left">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectAllPsychotestQuestions}
                    onChange={toggleSelectAllPsychotestQuestions}
                  />
                  Pilih Semua
                </label>
                <button className="btn-danger" onClick={bulkDeleteSelectedPsychotestQuestions}>Hapus Terpilih</button>
              </div>
            </div>

            <div className="bulk-actions" style={{ marginTop: '4px' }}>
              <label className="upload-label">
                📤 Upload Excel Soal Psikotest (A/B):
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setPsychotestExcelFile(e.target.files?.[0] || null)}
                />
              </label>
              <button className="btn-primary" onClick={uploadPsychotestExcelFile}>Upload Excel</button>
              
            </div>

            <div className="bulk-actions">
              <div className="bulk-actions-left">
                <a className="btn-info link-btn" href="/templates/Template_Psikotest_AB.xlsx" download>
                  ⬇️ Template Psikotest A/B
                </a>
                <label className="upload-label">
                  Show List:
                  <select value={psychotestPerPage} onChange={(e) => setPsychotestPerPage(e.target.value)}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="all">All</option>
                  </select>
                </label>
                <button type="button" className="btn-info" onClick={exportPsychotestExcel}>
                  ⬇️ Export Excel
                </button>
              </div>
            </div>

            <div style={{fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px'}}>
              Format Excel disarankan: Kolom A=Dimensi A, B=Pernyataan A, C=Dimensi B, D=Pernyataan B, E=Status (opsional: aktif/nonaktif)
            </div>

            {loadingPsychotest ? (
              <div className="loading-text">Memuat soal psikotest...</div>
            ) : (
              <div className="questions-table-container">
                <table className="questions-table">
                  <thead>
                    <tr>
                      <th style={{width: '40px'}}>
                        <input
                          type="checkbox"
                          checked={selectAllPsychotestQuestions}
                          onChange={toggleSelectAllPsychotestQuestions}
                        />
                      </th>
                      <th style={{width: '50px'}}>ID</th>
                      <th>Dimensi A</th>
                      <th>Pernyataan A</th>
                      <th>Dimensi B</th>
                      <th>Pernyataan B</th>
                      <th style={{width: '80px'}}>Status</th>
                      <th style={{width: '100px'}}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPsychotestQuestions.map((q) => (
                      <tr key={q.id} className={selectedPsychotestQuestionIds.includes(q.id) ? 'selected' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedPsychotestQuestionIds.includes(q.id)}
                            onChange={(e) => togglePsychotestQuestionSelection(q.id, e.target.checked)}
                          />
                        </td>
                        <td>{q.id}</td>
                        <td>{PSY_DIMENSIONS.find((d) => d.value === q.dimension_a)?.label || q.dimension_a}</td>
                        <td>{q.statement_a}</td>
                        <td>{PSY_DIMENSIONS.find((d) => d.value === q.dimension_b)?.label || q.dimension_b}</td>
                        <td>{q.statement_b}</td>
                        <td>{Number(q.is_active) === 1 ? 'Aktif' : 'Nonaktif'}</td>
                        <td>
                          <div className="td-actions">
                            <button className="btn-sm btn-secondary" onClick={() => startEditPsychotestQuestion(q)}>Edit</button>
                            <button className="btn-sm btn-danger" onClick={() => deletePsychotestQuestion(q.id)}>Hapus</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {psychotestQuestions.length === 0 && (
                      <tr><td colSpan="8" className="empty-row">Belum ada soal psikotest</td></tr>
                    )}
                  </tbody>
                </table>

                {psychotestQuestions.length > 0 && totalPsychotestPages > 1 && (
                  <div className="pagination">
                    <button
                      className="pagination-btn"
                      onClick={() => setPsychotestPage(Math.max(1, psychotestPage - 1))}
                      disabled={psychotestPage === 1}
                    >
                      ← Sebelumnya
                    </button>
                    <div className="pagination-info">
                      Halaman {psychotestPage} dari {totalPsychotestPages} (Total: {psychotestQuestions.length} soal)
                    </div>
                    <button
                      className="pagination-btn"
                      onClick={() => setPsychotestPage(Math.min(totalPsychotestPages, psychotestPage + 1))}
                      disabled={psychotestPage === totalPsychotestPages}
                    >
                      Berikutnya →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'recruiters' && user?.role === 'admin' && (
          <div className="exam-mgmt">
            <div className="mgmt-header">
              <h2>Kelola Recruiter</h2>
              <button className="btn-add" onClick={() => openRecruiterForm()}>
                ➕ Tambah Recruiter
              </button>
            </div>

            {showRecruiterForm && (
              <div className="form-modal">
                <div className="form-box" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>{editingRecruiterId ? 'Edit Recruiter' : 'Recruiter Baru'}</h3>
                    <button className="modal-close-btn" onClick={() => setShowRecruiterForm(false)}>✕</button>
                  </div>
                  <div className="form-group">
                    <label>Nama</label>
                    <input
                      type="text"
                      value={recruiterForm.name}
                      onChange={(e) => setRecruiterForm({ ...recruiterForm, name: e.target.value })}
                      placeholder="Nama recruiter"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={recruiterForm.email}
                      onChange={(e) => setRecruiterForm({ ...recruiterForm, email: e.target.value })}
                      placeholder="email@company.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Password {editingRecruiterId ? <span style={{fontWeight:400, color:'var(--text-muted)'}}>(opsional, isi jika ingin ganti)</span> : ''}</label>
                    <input
                      type="password"
                      value={recruiterForm.password}
                      onChange={(e) => setRecruiterForm({ ...recruiterForm, password: e.target.value })}
                      placeholder={editingRecruiterId ? 'Kosongkan jika tidak diubah' : 'Minimal 6 karakter'}
                    />
                  </div>
                  <div className="form-actions">
                    <button className="btn-save" onClick={saveRecruiter}>Simpan</button>
                    <button className="btn-cancel" onClick={() => setShowRecruiterForm(false)}>Batal</button>
                  </div>
                </div>
              </div>
            )}

            {loadingRecruiters ? (
              <div className="loading-text">Memuat data recruiter...</div>
            ) : (
              <div className="sessions-table">
                <table>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Nama</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recruiters.map((rec, idx) => (
                      <tr key={rec.id}>
                        <td>{idx + 1}</td>
                        <td>{rec.name}</td>
                        <td>{rec.email}</td>
                        <td>{rec.role}</td>
                        <td>
                          <div className="td-actions-row">
                            <button className="btn-secondary" onClick={() => openRecruiterForm(rec)}>Edit</button>
                            <button className="btn-danger" onClick={() => removeRecruiter(rec.id)}>Hapus</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {recruiters.length === 0 && (
                      <tr><td colSpan="5" className="empty-row">Belum ada recruiter</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {activeTab === 'positions' && (
          <div className="exam-mgmt">
            <div className="mgmt-header">
              <h2>Kelola Posisi</h2>
              <button className="btn-add" onClick={() => openPositionsForm()}>
                ➕ Tambah Posisi
              </button>
            </div>

            {showPositionForm && (
              <div className="form-modal">
                <div className="form-box" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>{editingPositionId ? 'Edit Master Data Posisi' : 'Add Data Posisi'}</h3>
                    <button className="modal-close-btn" onClick={() => setShowPositionForm(false)}>✕</button>
                  </div>
                  <div className="form-group">
                    <label>Position Code</label>
                    <input
                      type="text"
                      value={positionForm.position_cd}
                      onChange={(e) => setPositionForm({ ...positionForm, position_cd: e.target.value })}
                      placeholder=""
                    />
                  </div>
                  <div className="form-group">
                    <label>Position Name</label>
                    <input
                      type="text"
                      value={positionForm.position_name}
                      onChange={(e) => setPositionForm({ ...positionForm, position_name: e.target.value })}
                      placeholder=""
                    />
                  </div>
                  <div className="form-group">
                    <label>Departement</label>
                    <input
                      type="text"
                      value={positionForm.departement}
                      onChange={(e) => setPositionForm({ ...positionForm, departement: e.target.value })}
                    />
                  </div>
                  <div className="form-actions">
                    <button className="btn-save" onClick={savePosition}>Simpan</button>
                    <button className="btn-cancel" onClick={() => setShowPositionForm(false)}>Batal</button>
                  </div>
                </div>
              </div>
            )}

            {loadingPositions ? (
              <div className="loading-text">Memuat data positions...</div>
            ) : (
              <div className="sessions-table">
                <table>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>position Code</th>
                      <th>Position Name</th>
                      <th>Departement</th>
                      <th>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map((p, idx) => (
                      <tr key={p.id}>
                        <td>{idx + 1}</td>
                        <td>{p.position_cd}</td>
                        <td>{p.position_name}</td>
                        <td>{p.departement}</td>
                        <td>
                          <div className="td-actions-row">
                            <button className="btn-secondary" onClick={() => openPositionsForm(p)}>Edit</button>
                            <button className="btn-danger" onClick={() => removeMasterPosition(p.id)}>Hapus</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {positions.length === 0 && (
                      <tr><td colSpan="5" className="empty-row">Belum ada Data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {showUploadInstructions && (
          <div className="form-modal" style={{ zIndex: 1101 }} onClick={() => setShowUploadInstructions(false)}>
            <div className="form-box large modal-box" onClick={(e) => e.stopPropagation()}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid var(--border)'}}>
                <div style={{textAlign: 'left', flex: 1}}>
                  <h3 style={{marginBottom: '8px', marginTop: 0}}>
                    {uploadInstructionsType === 'multiple-choice' ? '📝 Panduan Upload Soal Pilihan Ganda' : '✍️ Panduan Upload Soal Essay'}
                  </h3>
                  <p style={{color: 'var(--text-muted)', fontSize: '13px', margin: 0}}>
                    Ikuti format di bawah ini untuk upload Excel soal
                  </p>
                </div>
                <button 
                  type="button"
                  onClick={() => setShowUploadInstructions(false)}
                  className="modal-close-btn"
                >
                  ✕
                </button>
              </div>
              {uploadInstructionsType === 'multiple-choice' ? (
                <div className="upload-guide">
                  <div className="guide-section">
                    <h4>📋 Format File Excel</h4>
                    <p>File harus berformat <strong>.xlsx</strong> atau <strong>.xls</strong> dengan kolom berikut:</p>
                    <div className="guide-columns">
                      <div className="column-item"><strong>Kolom A</strong><br/>Pertanyaan</div>
                      <div className="column-item"><strong>Kolom B</strong><br/>Opsi A</div>
                      <div className="column-item"><strong>Kolom C</strong><br/>Opsi B</div>
                      <div className="column-item"><strong>Kolom D</strong><br/>Opsi C</div>
                      <div className="column-item"><strong>Kolom E</strong><br/>Opsi D</div>
                      <div className="column-item"><strong>Kolom F</strong><br/>Opsi E (opsional)</div>
                      <div className="column-item"><strong>Kolom G</strong><br/>Jawaban (a/b/c/d/e)</div>
                      <div className="column-item"><strong>Kolom H</strong><br/>Poin</div>
                    </div>
                  </div>
                  <div className="guide-section">
                    <h4>📊 Contoh Data</h4>
                    <table className="example-table">
                      <thead>
                        <tr>
                          <th>Pertanyaan</th>
                          <th>A</th>
                          <th>B</th>
                          <th>C</th>
                          <th>D</th>
                          <th>E</th>
                          <th>Kunci</th>
                          <th>Poin</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Contoh pertanyaan 1</td>
                          <td>Option A</td>
                          <td>Option B</td>
                          <td>Option C</td>
                          <td>Option D</td>
                          <td>Option E</td>
                          <td><strong>a</strong></td>
                          <td>5</td>
                        </tr>
                        <tr>
                          <td>Contoh pertanyaan 2</td>
                          <td>Option A</td>
                          <td>Option B</td>
                          <td>Option C</td>
                          <td>Option D</td>
                          <td>-</td>
                          <td><strong>b</strong></td>
                          <td>5</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="guide-section" style={{backgroundColor: '#eff6ff', padding: '12px', borderRadius: '6px', border: '1px solid #bfdbfe'}}>
                    <h4 style={{marginTop: 0}}>💡 Tips</h4>
                    <ul className="guide-list">
                      <li>Kolom E (Opsi E) boleh kosong jika hanya ada 4 opsi</li>
                      <li>Jawaban ditulis dengan huruf kecil: a, b, c, d, atau e</li>
                      <li>Poin harus berupa angka positif</li>
                      <li>Tidak ada baris header di file Excel, langsung data</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="upload-guide">
                  <div className="guide-section">
                    <h4>📋 Format File Excel</h4>
                    <p>File harus berformat <strong>.xlsx</strong> atau <strong>.xls</strong> dengan kolom berikut:</p>
                    <div className="guide-columns">
                      <div className="column-item"><strong>Kolom A</strong><br/>Pertanyaan</div>
                      <div className="column-item"><strong>Kolom B</strong><br/>Rubrik Penilaian</div>
                      <div className="column-item"><strong>Kolom C</strong><br/>Poin Maksimal</div>
                    </div>
                  </div>
                  <div className="guide-section">
                    <h4>📊 Contoh Data</h4>
                    <table className="example-table">
                      <thead>
                        <tr>
                          <th>Pertanyaan</th>
                          <th>Rubrik Penilaian</th>
                          <th>Poin Maksimal</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Jelaskan konsep pemrograman berorientasi objek</td>
                          <td>Jelas & lengkap (OOP): 10 pts, Cukup jelas: 7 pts, Kurang: 3 pts</td>
                          <td>10</td>
                        </tr>
                        <tr>
                          <td>Bagaimana cara mengatasi bug dalam kode?</td>
                          <td>Metode sistematis: 8 pts, Asal mencoba: 4 pts</td>
                          <td>8</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="guide-section" style={{backgroundColor: '#fef9c3', padding: '12px', borderRadius: '6px', border: '1px solid #fde047'}}>
                    <h4 style={{marginTop: 0}}>💡 Penting untuk Essay</h4>
                    <ul className="guide-list">
                      <li>✅ Soal essay dinilai secara manual oleh recruiter</li>
                      <li>✅ Rubrik penilaian membantu recruiter dalam mengevaluasi jawaban</li>
                      <li>✅ Poin Maksimal adalah skor tertinggi yang bisa dicapai</li>
                      <li>✅ Tidak ada opsi pilihan (A-E) untuk soal essay</li>
                      <li>✅ Tidak ada kolom untuk jawaban benar</li>
                    </ul>
                  </div>
                </div>
              )}
              <div className="form-actions" style={{justifyContent: 'center'}}>
                <button type="button" className="btn-primary" onClick={() => setShowUploadInstructions(false)} style={{maxWidth: '200px'}}>
                  ✓ Saya Sudah Mengerti
                </button>
              </div>
            </div>
          </div>
        )}
        {showExamQuestionsModal && examModalExam && (
          <div className="form-modal" onClick={() => setShowExamQuestionsModal(false)}>
            <div className="form-box large modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h3>Soal Ujian: {examModalExam.title}</h3>
                  <p style={{margin: '8px 0 0', fontSize: '13px', color: 'var(--text-muted)'}}>Tipe: {examModalExam.multiple_choice === 1 ? 'Pilihan Ganda' : 'Essay'} • Posisi: {examModalExam.position === 'all' ? 'Semua Posisi' : examModalExam.position}</p>
                </div>
                <button className="modal-close-btn" onClick={() => setShowExamQuestionsModal(false)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="detail-grid">
                  <div>
                    <p className="detail-label">Durasi</p>
                    <p className="detail-value">{examModalExam.duration_minutes} menit</p>
                  </div>
                  <div>
                    <p className="detail-label">Min Score</p>
                    <p className="detail-value">{examModalExam.passing_grade}%</p>
                  </div>
                  <div>
                    <p className="detail-label">Total Soal</p>
                    <p className="detail-value">{questions.length} soal</p>
                  </div>
                  <div>
                    <p className="detail-label">Show to Kandidat</p>
                    <p className="detail-value">{examModalExam.total > 0 ? Math.min(examModalExam.total, examModalExam.total_questions) : examModalExam.total_questions} soal</p>
                  </div>
                </div>

                <div className="questions-header" style={{padding: '0', marginTop: '20px', justifyContent: 'space-between', alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: '12px'}}>
                  <div>
                    <h4 style={{margin: '0 0 6px'}}>Manage Soal</h4>
                    <p style={{margin: 0, fontSize: '13px', color: 'var(--text-muted)'}}>Edit, tambah, upload, dan hapus soal terpilih langsung dari modal ini.</p>
                  </div>
                  <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                    <button type="button" className="btn-info" onClick={() => {
                      setUploadInstructionsType(examModalExam.multiple_choice === 1 ? 'multiple-choice' : 'essay');
                      setShowUploadInstructions(true);
                    }}>
                      ℹ️ Panduan Upload
                    </button>
                    <button type="button" className="btn-add-question" onClick={() => {
                      setEditingQuestion(null);
                      setQuestionForm({
                        question_text: '',
                        option_a: '',
                        option_b: '',
                        option_c: '',
                        option_d: '',
                        option_e: '',
                        correct_answer: 'a',
                        points: 10
                      });
                      setShowQuestionForm(true);
                    }}>
                      ➕ Tambah Soal
                    </button>
                    <button className="btn-danger" onClick={bulkDeleteSelectedQuestions}>Hapus Terpilih</button>
                  </div>
                </div>

                <div className="bulk-actions" style={{marginTop: '16px'}}>
                  <label className="upload-label">
                    📤 Upload Excel soal:
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  <button className="btn-primary" onClick={uploadQuestionsExcelFile}>Upload Excel</button>
                </div>

                <div className="bulk-actions" style={{marginTop: '12px'}}>
                  <div className="bulk-actions-left">
                    <a className="btn-info link-btn" href="/templates/Template_Exam_Pilihan_Ganda.xlsx" download>
                      ⬇️ Template Exam Pilihan Ganda
                    </a>
                    
                    <label className="upload-label">
                      Show List:
                      <select value={questionPerPage} onChange={(e) => setQuestionPerPage(e.target.value)}>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="all">All</option>
                      </select>
                    </label>
                    <button type="button" className="btn-info" onClick={exportExamQuestionsExcel}>
                      ⬇️ Export Excel
                    </button>
                  </div>
                </div>

                {showQuestionForm && selectedExamForQuestions === examModalExam.id && (
                  <div className="form-modal">
                    <div className="form-box large">
                      <div className="modal-header">
                        <h3>
                          {editingQuestion ? 'Edit Soal' : 'Soal Baru'}
                          <span style={{fontSize: '13px', fontWeight: 400, color: 'var(--text-muted)', marginLeft: '10px'}}>
                            {examModalExam.multiple_choice === 1 ? '📝 Pilihan Ganda' : '✍️ Essay'}
                          </span>
                        </h3>
                        <button className="modal-close-btn" onClick={() => setShowQuestionForm(false)}>✕</button>
                      </div>
                      <div className="form-group">
                        <label>Pertanyaan</label>
                        <textarea
                          value={questionForm.question_text}
                          onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                          placeholder="Masukkan pertanyaan..."
                          rows="3"
                        />
                      </div>
                      {examModalExam.multiple_choice === 1 ? (
                        <>
                          <div className="form-group">
                            <label>Opsi A</label>
                            <input type="text" value={questionForm.option_a} onChange={(e) => setQuestionForm({...questionForm, option_a: e.target.value})} placeholder="Opsi A" />
                          </div>
                          <div className="form-group">
                            <label>Opsi B</label>
                            <input type="text" value={questionForm.option_b} onChange={(e) => setQuestionForm({...questionForm, option_b: e.target.value})} placeholder="Opsi B" />
                          </div>
                          <div className="form-group">
                            <label>Opsi C</label>
                            <input type="text" value={questionForm.option_c} onChange={(e) => setQuestionForm({...questionForm, option_c: e.target.value})} placeholder="Opsi C" />
                          </div>
                          <div className="form-group">
                            <label>Opsi D</label>
                            <input type="text" value={questionForm.option_d} onChange={(e) => setQuestionForm({...questionForm, option_d: e.target.value})} placeholder="Opsi D" />
                          </div>
                          <div className="form-group">
                            <label>Opsi E <span style={{fontWeight:400, color:'var(--text-muted)'}}>(opsional)</span></label>
                            <input type="text" value={questionForm.option_e} onChange={(e) => setQuestionForm({...questionForm, option_e: e.target.value})} placeholder="Opsi E (kosongkan jika tidak ada)" />
                          </div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Jawaban Benar</label>
                              <select value={questionForm.correct_answer} onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}>
                                <option value="a">A</option>
                                <option value="b">B</option>
                                <option value="c">C</option>
                                <option value="d">D</option>
                                <option value="e">E</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Poin</label>
                              <input type="number" value={questionForm.points} onChange={(e) => setQuestionForm({...questionForm, points: parseInt(e.target.value)})} min="1" />
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="essay-info-box">
                            <span>✍️</span>
                            <div>
                              <strong>Soal Essay</strong>
                              <p>Recruiter akan mengoreksi dan memberikan nilai secara manual setelah kandidat mengerjakan ujian.</p>
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Rubrik Penilaian <span style={{fontWeight:400, color:'var(--text-muted)'}}>(panduan untuk recruiter)</span></label>
                            <textarea
                              value={questionForm.option_a}
                              onChange={(e) => setQuestionForm({...questionForm, option_a: e.target.value})}
                              placeholder="Contoh: Jawaban lengkap dan tepat: 100%, Sebagian benar: 50-70%, Tidak relevan: 0%"
                              rows="3"
                            />
                          </div>
                          <div className="form-group">
                            <label>Poin Maksimal</label>
                            <input type="number" value={questionForm.points} onChange={(e) => setQuestionForm({...questionForm, points: parseInt(e.target.value)})} min="1" />
                          </div>
                        </>
                      )}
                      <div className="form-group">
                        <label>Foto Soal <span style={{fontWeight:400, color:'var(--text-muted)'}}>(opsional)</span></label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setQuestionPhotoFile(file);
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = () => setQuestionPhotoPreview(reader.result);
                              reader.readAsDataURL(file);
                            } else {
                              setQuestionPhotoPreview(null);
                            }
                          }}
                        />
                        {questionPhotoPreview && (
                          <div className="preview-box">
                            <img src={questionPhotoPreview} alt="Preview soal" />
                          </div>
                        )}
                      </div>
                      <div className="form-actions">
                        <button className="btn-save" onClick={saveQuestion}>Simpan</button>
                        <button className="btn-cancel" onClick={() => setShowQuestionForm(false)}>Batal</button>
                      </div>
                    </div>
                  </div>
                )}

                {loadingQuestions ? (
                  <div className="loading-text">Memuat soal...</div>
                ) : questions.length === 0 ? (
                  <div className="empty-state">Belum ada soal untuk ujian ini.</div>
                ) : (
                  <div className="questions-table-container" style={{marginTop: '16px'}}>
                    <table className="questions-table">
                      <thead>
                        <tr>
                          <th style={{width: '30px'}}>
                            <input
                              type="checkbox"
                              checked={selectAllQuestions}
                              onChange={toggleSelectAllQuestions}
                            />
                          </th>
                          <th style={{width: '50px'}}>No</th>
                          <th>Pertanyaan</th>
                          <th style={{width: '100px'}}>Tipe</th>
                          <th>Opsi & Jawaban</th>
                          <th style={{width: '60px'}}>Poin</th>
                          <th style={{width: '80px'}}>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedQuestions.map((q, idx) => (
                          <tr key={q.id} className={selectedQuestionIds.includes(q.id) ? 'selected' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedQuestionIds.includes(q.id)}
                                onChange={(e) => toggleQuestionSelection(q.id, e.target.checked)}
                              />
                            </td>
                            <td>{((questionPage - 1) * effectiveQuestionPerPage) + idx + 1}</td>
                            <td>
                              <div className="td-question">
                                {q.photo && <span className="q-photo-badge">📸</span>}
                                <span className="q-text-preview">{q.question_text.substring(0, 80)}{q.question_text.length > 80 ? '...' : ''}</span>
                              </div>
                            </td>
                            <td>
                              <span className={`badge badge-${q.correct_answer ? 'mc' : 'essay'}`}>
                                {q.correct_answer ? '📝 Pilihan Ganda' : '✍️ Essay'}
                              </span>
                            </td>
                            <td>
                              {q.correct_answer ? (
                                <div className="td-options">
                                  {['a', 'b', 'c', 'd', 'e'].map(opt => {
                                    const value = q[`option_${opt}`];
                                    if (!value) return null;
                                    return (
                                      <div key={opt} className={`option-item ${q.correct_answer === opt ? 'correct-option' : ''}`}>
                                        <strong>{opt.toUpperCase()}:</strong> {value.substring(0, 40)}{value.length > 40 ? '...' : ''}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div>
                                  <span className="essay-label">✍️ Essay — Recruiter menilai</span>
                                  {q.option_a && (
                                    <div style={{fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px'}}>
                                      📋 Rubrik: {q.option_a.substring(0, 60)}{q.option_a.length > 60 ? '...' : ''}
                                    </div>
                                  )}
                                </div>
                              )}
                            </td>
                            <td><strong>{q.points}</strong></td>
                            <td>
                              <div className="td-actions">
                                <button className="btn-sm btn-secondary" onClick={() => startEditQuestion(q)}>Edit</button>
                                <button className="btn-sm btn-danger" onClick={() => deleteQuestion(q.id)}>Hapus</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {totalPages > 1 && (
                      <div className="pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => setQuestionPage(Math.max(1, questionPage - 1))}
                          disabled={questionPage === 1}
                        >
                          ← Sebelumnya
                        </button>
                        <div className="pagination-info">
                          Halaman {questionPage} dari {totalPages} (Total: {questions.length} soal)
                        </div>
                        <button
                          className="pagination-btn"
                          onClick={() => setQuestionPage(Math.min(totalPages, questionPage + 1))}
                          disabled={questionPage === totalPages}
                        >
                          Berikutnya →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
